import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, Menu, X, Search, Phone, MapPin, Send, Trash2 } from 'lucide-react';
import './App.css';

// ===== IMPORTS (Keep UI identical) =====
const shopLocations = [
  { name: 'Graceland Shopping Mall', address: 'Shop 117, Irete, Owerri', phone: '+2348037891888', whatsapp: '+2347055921564', hours: '9AM-7PM' },
  { name: 'Ritchie & Hills Plaza', address: 'Suite 03, Egbeada, Owerri', phone: '+2348037891888', whatsapp: '+2347055921564', hours: '9AM-7PM' },
];

// Emoji lookup for category tiles on the homepage
const CATEGORY_EMOJIS = {
  'Nkatanni Brand': '⭐',
  'Soft Drinks & Juices': '🥤',
  'Natural Drinks & Water': '🧃',
  'Tea, Coffee & Hot Drinks': '☕',
  'Malt Drinks': '🍺',
  'Dairy & Breakfast': '🥛',
  'Rice, Beans & Grains': '🌾',
  'Flours & Swallows': '🍲',
  'Pasta & Noodles': '🍜',
  'Oils, Sauces & Condiments': '🫒',
  'Spices & Soup Ingredients': '🌶️',
  'Seafood, Fish & Meat': '🐟',
  'Tinned Foods': '🥫',
  'Snacks, Biscuits & Chocolate': '🍫',
  'Baking Essentials': '🧁',
  'Local Deli': '🥬',
  'Household Essentials': '🧼',
  'Fresh Produce': '🍎',
};

// Products Nkatanni makes/packages themselves (from Products/1 Nkatanni Brand folder)
const NKATANNI_BRAND = new Set([
  'Abacha', 'Achi Powder', 'All-purpose flour', 'All Purpose Flour', 'All-Purpose Seasoning', 'Beans Flour',
  'Brown Beans', 'Catfish Cutlets', 'Catfish Whole', 'Chicken Seasoning',
  'Cocoyam Flour', 'Crayfish', 'Date Syrup', 'Dried Asa Fish',
  'Dried Onions', 'Dried Pepper', 'Dried Prawns (Oporo)', 'Dried Shombo',
  'Dried Tatashe', 'Egusi', 'Fried Rice Spice', 'Fufu', 'Garri (White)',
  'Garri (Yellow)', 'Ghana Pepper', 'Ginger Pwder', 'Ginger Powder',
  'Gut Tonic Juice', 'Honey', 'Honey Beans', 'Mixed Grain Pap',
  'Oatmeal Flour', 'Ofo', 'Ogbono', 'OGG Seasoning', 'Okpa Flour',
  'Palm Oil', 'Peppersoup Spices', 'Plantain Flour', 'Potato Flour',
  'Rice', 'Seafood Seasoning', 'Smokey Jollof Seasoning', 'Smoky Jollof Seasoning', 'Dried Ukwa',
  'Sunny Side Juice', 'Sunnyside Juice', 'Suya Spices', 'Tigernut Drink',
  'Tumeric Powder', 'White Beans', 'Yam Flour', 'Yummy Parfait',
  'Yummy Yoghurt Strawberry', 'Yummy Yorghurt 450ml', 'Yummy Yorghurt Banana',
  'Zobo Drink', 'Zobo Leaves',
]);
const isNkatanniBrand = (p) => NKATANNI_BRAND.has(p.name);

// ===== SUPABASE CLIENT =====
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://blmlfdamxuunwxjnupjb.supabase.co';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJsbWxmZGFteHV1bnd4am51cGpiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk4MzA2OTksImV4cCI6MjA5NTQwNjY5OX0.T_BvZC8EJ0zFvgbl0UAcPW5tegxVjKYPSVy1FBwTq1Y';

// ===== ADMIN SESSION (Supabase Auth) =====
// Admin signs in with email + password checked by Supabase; no password lives in this code.
// The session sits in sessionStorage, so it ends when the browser tab closes.
const SESSION_KEY = 'nkatanni_admin_session';
const getSession = () => { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)); } catch (e) { return null; } };
const setSession = (sess) => { try { if (sess) sessionStorage.setItem(SESSION_KEY, JSON.stringify(sess)); else sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* storage unavailable */ } };

const authRequest = async (grantType, body) => {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=${grantType}`, {
    method: 'POST',
    headers: { apikey: SUPABASE_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error_description || json.msg || 'Login failed');
  const sess = { access_token: json.access_token, refresh_token: json.refresh_token, email: json.user && json.user.email, expires_at: Date.now() + (json.expires_in || 3600) * 1000 };
  setSession(sess);
  return sess;
};
const adminSignIn = (email, password) => authRequest('password', { email, password });
const adminSignOut = () => setSession(null);

// Valid admin access token (refreshed when close to expiry), or null when not signed in
const getAccessToken = async () => {
  let sess = getSession();
  if (!sess) return null;
  if (Date.now() > sess.expires_at - 60000) {
    try { sess = await authRequest('refresh_token', { refresh_token: sess.refresh_token }); }
    catch (e) { setSession(null); return null; }
  }
  return sess.access_token;
};

// ===== SUPABASE CLIENT =====
// Customers use the public key (read-only + placing orders). A signed-in admin's token unlocks edits.
const supabaseFetch = async (url, options = {}) => {
  const token = (await getAccessToken()) || SUPABASE_KEY;
  const headers = { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  try {
    const response = await fetch(url, { ...options, headers: { ...headers, ...options.headers } });
    const text = await response.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
    if (!response.ok) return { data: null, error: new Error((data && (data.message || data.hint)) || `Request failed (${response.status})`) };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
};

// Update/delete that matches no rows is how the database answers a non-admin, so treat it as an error
const mustChange = async (request) => {
  const result = await request;
  if (!result.error && Array.isArray(result.data) && result.data.length === 0) {
    return { data: null, error: new Error('Nothing was changed. Your admin login may have expired, so please log in again.') };
  }
  return result;
};

const IMAGE_BUCKET = 'product-images';
const WA_CHANNEL = 'https://whatsapp.com/channel/0029Va8WbI59mrGj2ZWdRv1P';
const GOOGLE_REVIEW = 'https://g.page/r/CYxMDGRCLBCcEBM/review';
// Meta Pixel helpers — content_ids match the catalogue (NK-0001)
const nkId = (id) => `NK-${String(id).split('-')[0].padStart(4, '0')}`;
const fb = (ev, params) => { try { if (typeof window.fbq === 'function') window.fbq('track', ev, params); } catch (e) {} };
const reviewUs = (where) => { try { if (window.gtag) window.gtag('event','google_review_click',{placement:where}); } catch(e){} window.open(GOOGLE_REVIEW,'_blank'); };
const joinChannel = (where) => { try { if (window.gtag) window.gtag('event', 'join_whatsapp_channel', { placement: where }); } catch (e) {} window.open(WA_CHANNEL, '_blank'); };
const FREE_DELIVERY_MIN = 100000;
const DELIVERY_FEE = 2500;
// Delivery areas: edit fees here. fee null = quoted on WhatsApp. free = qualifies for free delivery over FREE_DELIVERY_MIN
const DELIVERY_ZONES = [
  { id: 'owerri', label: 'Owerri', fee: 2500, free: true },
  { id: 'imo', label: 'Elsewhere in Imo State', fee: 4000, free: true },
  { id: 'nigeria', label: 'Other states in Nigeria', fee: 7500, free: true },
  { id: 'pickup', label: 'Pick up at our store', fee: 0, free: false },
  { id: 'intl', label: 'Outside Nigeria (quote on WhatsApp)', fee: null, free: false },
];
const loadCustomer = () => { try { return JSON.parse(localStorage.getItem('nk_customer')) || {}; } catch (e) { return {}; } };
const naira = (n) => `₦${Math.max(0, Math.round(n)).toLocaleString()}`;
const imgSrc = (image) => !image ? '/logo192.png' : /^https?:\/\//.test(image) ? image : `/assets/${image}`;
const isOutOfStock = (p) => p && p.in_stock === false;

// ===== INVOICES (PDF made in the browser with jsPDF, loaded on demand) =====

// Customer FAQs (source: "Nkatanni Customer FAQs" doc, checked by Ellen 25 Oct 2026)
const FAQS = [
  { s: 'Ordering and payment', q: 'How do I place an order?', a: 'Browse nkatannifoodmart.com, add items to your cart and tap checkout. Your order is sent to our WhatsApp, and we reply to confirm prices, the delivery fee and your total.' },
  { s: 'Ordering and payment', q: 'Can I order straight on WhatsApp?', a: 'Yes. Message 0705 592 1564 with your list, or pick items from our WhatsApp catalogue.' },
  { s: 'Ordering and payment', q: 'How do I pay?', a: 'By bank transfer to Moniepoint MFB, account name Nkatanni Food Mart, account number 4006194407. Use your invoice number as the transfer reference. In the store you can also pay by POS or cash.' },
  { s: 'Ordering and payment', q: 'When is my order confirmed?', a: 'As soon as payment is received. Payment is due before delivery.' },
  { s: 'Ordering and payment', q: 'Will I get an invoice?', a: 'Yes. After checkout you can download a PDF invoice with your order number and our bank details.' },
  { s: 'Ordering and payment', q: 'Do your prices include VAT?', a: 'Yes, all prices are VAT inclusive. Prices may change, so we confirm your total on WhatsApp before you pay.' },
  { s: 'Ordering and payment', q: 'Do you have discounts or promo codes?', a: 'Check the Daily Specials and Combos on our homepage. We share promo codes on our WhatsApp Status and Instagram when they run.' },
  { s: 'Delivery and pickup', q: 'Do you deliver?', a: 'Yes. We deliver across Owerri, and we confirm the delivery fee on WhatsApp with your order. It depends on your location; within Owerri it is usually ₦2,500. Delivery is free on orders over ₦100,000.' },
  { s: 'Delivery and pickup', q: 'How soon will my order arrive?', a: 'Orders paid early in the day are usually delivered the same day.' },
  { s: 'Delivery and pickup', q: 'Do you deliver outside Owerri?', a: 'Yes. We send orders nationwide by courier, and we quote the shipping fee on WhatsApp before you pay.' },
  { s: 'Delivery and pickup', q: 'Can I order from abroad?', a: 'Yes. We supply customers and African stores abroad. Send us your list on WhatsApp and we will quote packaging and shipping.' },
  { s: 'Delivery and pickup', q: 'Can I pick up my order? Where is your shop?', a: 'Yes. Visit either of our shops: Shop 117, Graceland Shopping Mall, Egbeada New Road, Irette, Owerri West, or Suite 03, Ritchie & Hills Plaza, Egbeada, Owerri. We are open from 9am to 7pm.' },
  { s: 'Products and quality', q: 'What do you sell?', a: 'Over 300 grocery items: foodstuff and soup ingredients, rice, beans and grains, swallows and flours, spices and seasonings, fish and meat, drinks, breakfast items and household essentials.' },
  { s: 'Products and quality', q: 'What is the Nkatanni brand?', a: 'Our own range, packed hygienically under the Nkatanni label. It includes egusi, ogbono, crayfish, spices, flours and garri, our natural drinks (Zobo, Tigernut, Gut Tonic and Sunny Side Juice), and Yummy Yoghurt and Parfait.' },
  { s: 'Products and quality', q: 'Are your drinks natural?', a: 'Yes. They are made from natural ingredients. For example, our Zobo is brewed from hibiscus leaves, ginger, cloves, star anise and pineapple. We add no preservatives; we preserve our drinks naturally with lemon and ginger juice.' },
  { s: 'Products and quality', q: 'How should I store the drinks and yoghurt, and how long do they last?', a: 'Unopened drinks last up to 10 days if kept refrigerated or frozen. Once opened, finish the drink the same day. Keep yoghurt and parfait refrigerated at all times.' },
  { s: 'Products and quality', q: 'What sizes do you have?', a: 'Most items come in several sizes, for example Abacha in 250g and 500g and Brown Beans from 750g to 3kg. Each product page shows the sizes and prices available.' },
  { s: 'Hampers, returns and contact', q: 'Do you sell food hampers?', a: "Yes. Our ready-made combos are the Sisi Combo (₦50,000), Mama's Combo (₦60,000) and Nepo Combo (₦60,000); see them at nkatannifoodmart.com/hampers. We also make custom hampers for families, corporate gifts and NGOs on request." },
  { s: 'Hampers, returns and contact', q: 'Can I return an item?', a: 'Goods once sold are not returnable, so please check your order on delivery. If an item arrives damaged or wrong, send us a photo on WhatsApp within 15 minutes of delivery and we will sort it out.' },
  { s: 'Hampers, returns and contact', q: 'How can I reach you?', a: 'WhatsApp 0705 592 1564, call 0803 789 1888, or find us as @nkatanni on Instagram, Facebook and TikTok. Enjoyed your order? Leave us a Google review.' },
];
const FAQ_SECTIONS = [...new Set(FAQS.map(f => f.s))];
const BANK = { bank: 'Moniepoint MFB', name: 'Nkatanni Food Mart', number: '4006194407' };
const waNumber = (p) => { const d = String(p || '').replace(/\D/g, ''); return d.startsWith('234') ? d : d.startsWith('0') ? '234' + d.slice(1) : d; };
const reminderText = (o) => {
  const first = String(o.customer_name || '').trim().split(/\s+/).filter(w => !/^(mr|mrs|ms|miss|dr|chief|engr|barr)\.?$/i.test(w))[0] || '';
  const greet = o.customer_name ? `Hello ${/^(mr|mrs|ms|miss|dr|chief|engr|barr)\.?\s/i.test(o.customer_name.trim()) ? o.customer_name.trim() : first},` : 'Hello,';
  return `${greet}\n\nThis is Nkatanni Food Mart. 🙏 A gentle reminder that payment for your order ${o.ref} of ₦${Number(o.total || 0).toLocaleString()} is still outstanding.\n\nPlease pay to:\n${BANK.bank}\nAccount no: ${BANK.number}\nAccount name: ${BANK.name}\nReference: ${o.ref}\n\nKindly send your payment receipt here once done. Thank you for choosing Nkatanni, your food partner!`;
};
const INVOICE_TERMS = 'Payment is due on order. Your order is confirmed once payment is received.';
const makeRef = () => 'NK-' + Date.now().toString(36).slice(-4).toUpperCase() + Math.random().toString(36).slice(2, 4).toUpperCase();
let _jsPdfLoading = null;
const loadJsPdf = () => {
  if (window.jspdf) return Promise.resolve(window.jspdf);
  if (!_jsPdfLoading) _jsPdfLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload = () => resolve(window.jspdf); s.onerror = () => { _jsPdfLoading = null; reject(new Error('Could not load the invoice tool. Check your internet and try again.')); };
    document.head.appendChild(s);
  });
  return _jsPdfLoading;
};
const loadLogo = () => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => { try { const c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight; c.getContext('2d').drawImage(img, 0, 0); resolve({ data: c.toDataURL('image/jpeg', 0.92), w: img.naturalWidth, h: img.naturalHeight }); } catch (e) { resolve(null); } };
  img.onerror = () => resolve(null);
  img.src = '/assets/NKATA%20NNI%20LOGO%20var2b.jpg';
});
const fmtPhone = (p) => { const d = String(p || '').replace(/\D/g, ''); return d.length === 11 && d[0] === '0' ? `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}` : (p || ''); };
const money = (v) => Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const parseOrderItems = (it) => { if (Array.isArray(it)) return it; try { const v = JSON.parse(it); return Array.isArray(v) ? v : []; } catch (e) { return []; } };
// Poppins fonts for the PDF (same look as the hand-made invoice); falls back to Helvetica if they can't load
const FONT_BASE = '/fonts/'; // public/fonts (Poppins, Open Font Licence)
let _fontCache = null;
const loadPdfFonts = async () => {
  if (_fontCache) return _fontCache;
  const toB64 = (buf) => { const bytes = new Uint8Array(buf); let bin = ''; for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000)); return btoa(bin); };
  try {
    const files = ['Poppins-Regular.ttf', 'Poppins-Medium.ttf', 'Poppins-Bold.ttf'];
    const data = await Promise.all(files.map(f => fetch(FONT_BASE + f).then(r => { if (!r.ok) throw new Error('font'); return r.arrayBuffer(); }).then((buf) => {
      const b = new Uint8Array(buf.slice(0, 4)); // a real TTF starts with 00 01 00 00 or "true" — not an HTML page
      const ok = (b[0] === 0 && b[1] === 1 && b[2] === 0 && b[3] === 0) || String.fromCharCode(...b) === 'true';
      if (!ok || buf.byteLength < 10000) throw new Error('not a font');
      return toB64(buf);
    })));
    _fontCache = { regular: data[0], medium: data[1], bold: data[2] };
  } catch (e) { _fontCache = null; }
  return _fontCache;
};
// inv: { ref, date, customer_name, customer_phone, delivery_area, address, items:[{name, qty, qty_label, price, amount}], subtotal, discount_amount, discount_label, delivery_fee, total, paid }
const downloadInvoice = async (inv) => {
  const { jsPDF } = await loadJsPdf();
  const [logo, fonts] = await Promise.all([loadLogo(), loadPdfFonts()]);
  let doc;
  try { doc = renderInvoice(jsPDF, inv, logo, fonts); } catch (e) { doc = renderInvoice(jsPDF, inv, logo, null); } // fall back to built-in font
  doc.save(`Invoice_${inv.ref}.pdf`);
};
const renderInvoice = (jsPDF, inv, logo, fonts) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  let FR = ['helvetica', 'normal'], FM = ['helvetica', 'bold'], FB = ['helvetica', 'bold'];
  if (fonts) {
    doc.addFileToVFS('Poppins-Regular.ttf', fonts.regular); doc.addFont('Poppins-Regular.ttf', 'Poppins', 'normal');
    doc.addFileToVFS('Poppins-Medium.ttf', fonts.medium); doc.addFont('Poppins-Medium.ttf', 'PoppinsMedium', 'normal');
    doc.addFileToVFS('Poppins-Bold.ttf', fonts.bold); doc.addFont('Poppins-Bold.ttf', 'Poppins', 'bold');
    FR = ['Poppins', 'normal']; FM = ['PoppinsMedium', 'normal']; FB = ['Poppins', 'bold'];
  }
  const W = 595.28, H = 841.89, M = 40;
  const G = [34, 104, 73], LG = [238, 245, 240], OR = [247, 148, 29], INK = [43, 43, 43], GR = [107, 107, 107], LN = [217, 227, 220];
  const col = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const f = (face, size) => { doc.setFont(face[0], face[1]); doc.setFontSize(size); };
  const date = inv.date ? new Date(inv.date) : new Date();
  const d = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  // header
  if (logo) doc.addImage(logo.data, 'JPEG', M, 92 - 190 * logo.h / logo.w, 190, 190 * logo.h / logo.w);
  col(G); f(FB, 26); doc.text('INVOICE', W - M, 62, { align: 'right' });
  f(FB, 13); doc.text('NKATANNI FOOD MART', M, 128);
  col(GR); f(FR, 8.5);
  doc.text('Shop 117, Graceland Shopping Mall, Egbeada New Road, Irette, Owerri West, Imo State', M, 142);
  doc.text('Call: 0803 789 1888   •   WhatsApp: 0705 592 1564   •   www.nkatannifoodmart.com', M, 154);
  [['Invoice No.', inv.ref], ['Invoice Date', d], ['Due Date', d]].forEach(([l, v], i) => {
    const y = 92 + i * 20;
    col(G); f(FM, 9); doc.text(l, W - M - 200, y);
    col(INK); f(FR, 9.5); doc.text(String(v || ''), W - M - 110, y);
    doc.setDrawColor(...LN); doc.setLineWidth(0.6); doc.line(W - M - 110, y + 4, W - M, y + 4);
  });
  doc.setDrawColor(...G); doc.setLineWidth(1); doc.line(M, 170, W - M, 170);
  // bill to
  doc.setFillColor(...LG); doc.roundedRect(M, 185, W - 2 * M, 78, 8, 8, 'F');
  col(G); f(FB, 9.5); doc.text('BILL TO', M + 12, 203);
  [['Customer name', inv.customer_name], ['Phone', fmtPhone(inv.customer_phone)], ['Address', [inv.address, inv.delivery_area].filter(Boolean).join(', ')]].forEach(([l, v], i) => {
    col(GR); f(FR, 8.5); doc.text(l, M + 12, 221 + i * 15);
    col(INK); f(FM, 9.5); doc.text(String(v || '—').slice(0, 85), M + 110, 221 + i * 15);
  });
  if (inv.paid) { doc.setDrawColor(...G); doc.setLineWidth(2); doc.roundedRect(W - M - 104, 198, 92, 28, 5, 5); col(G); f(FB, 15); doc.text('PAID', W - M - 58, 217, { align: 'center' }); }
  // items table
  doc.setFillColor(...G); doc.rect(M, 295, W - 2 * M, 20, 'F');
  doc.setTextColor(255, 255, 255); f(FB, 8);
  const cx = [M + 8, M + 38, M + 330, W - M - 8];
  doc.text('S/N', cx[0], 308.5); doc.text('ITEM DESCRIPTION', cx[1], 308.5); doc.text('QUANTITY', cx[2], 308.5); doc.text('AMOUNT (NGN)', cx[3], 308.5, { align: 'right' });
  let top = 315; const RH = 19;
  inv.items.forEach((it, i) => {
    if (top > H - 230) { doc.addPage(); top = 50; }
    if (i % 2 === 1) { doc.setFillColor(246, 250, 247); doc.rect(M, top, W - 2 * M, RH, 'F'); }
    doc.setDrawColor(...LN); doc.setLineWidth(0.5); doc.line(M, top + RH, W - M, top + RH);
    const amt = it.amount != null ? it.amount : (it.price || 0) * (it.qty || 1);
    col(INK); f(FR, 8.5);
    doc.text(String(i + 1), cx[0], top + 13); doc.text(String(it.name || '').slice(0, 62), cx[1], top + 13);
    doc.text(it.qty_label || String(it.qty || 1), cx[2], top + 13); doc.text(money(amt), cx[3], top + 13, { align: 'right' });
    top += RH;
  });
  // totals (right)
  const tY = top + 24, lx = W - M - 210; let ty = tY;
  const trow = (l, v) => { col(INK); f(FR, 9); doc.text(l, lx, ty); doc.text(v, W - M - 8, ty, { align: 'right' }); ty += 17; };
  trow('Subtotal', money(inv.subtotal));
  if (inv.discount_amount > 0) trow(`Discount${inv.discount_label ? ` (${inv.discount_label})` : ''}`, '-' + money(inv.discount_amount));
  trow(inv.delivery_area ? `Delivery fee (${String(inv.delivery_area).split(' ')[0]})` : 'Delivery fee', inv.delivery_fee == null ? 'To be quoted' : inv.delivery_fee === 0 ? 'FREE' : money(inv.delivery_fee));
  col(GR); f(FR, 7.5); doc.text('Prices are VAT inclusive.', lx, ty - 3);
  const boxTop = ty + 4;
  doc.setFillColor(...G); doc.roundedRect(lx - 10, boxTop, W - M - lx + 10, 28, 6, 6, 'F');
  doc.setTextColor(255, 255, 255); f(FB, 10); doc.text(inv.paid ? 'TOTAL PAID' : 'TOTAL DUE', lx, boxTop + 18.5); doc.text('NGN ' + money(inv.total), W - M - 8, boxTop + 18.5, { align: 'right' });
  // payment details (left)
  col(G); f(FB, 9.5); doc.text('PAYMENT DETAILS', M, tY + 6);
  doc.setDrawColor(...G); doc.setLineWidth(0.8); doc.line(M, tY + 11, M + 230, tY + 11);
  [['Bank', BANK.bank], ['Account name', BANK.name], ['Account no.', BANK.number]].forEach(([l, v], k) => {
    col(GR); f(FR, 8.5); doc.text(l, M, tY + 28 + k * 16);
    col(INK); f(k === 2 ? FB : FM, 9.5); doc.text(v, M + 80, tY + 28 + k * 16);
  });
  col(GR); f(FR, 8);
  doc.text(doc.splitTextToSize(`${INVOICE_TERMS} Please use ${inv.ref} as your transfer reference.`, 300), M, tY + 86);
  // footer
  doc.setDrawColor(...LN); doc.setLineWidth(0.6); doc.line(M, H - 58, W - M, H - 58);
  col(G); f(FM, 9); doc.text('Thank you for choosing Nkatanni — your food partner.', W / 2, H - 42, { align: 'center' });
  col(OR); f(FR, 8); doc.text('nkatannifoodmart.com  •  @nkatanni', W / 2, H - 29, { align: 'center' });
  return doc;
};
const orderToInvoice = (o) => {
  const items = parseOrderItems(o.items);
  const subtotal = o.subtotal || items.reduce((a, i) => a + (i.amount != null ? i.amount : (i.price || 0) * (i.qty || 1)), 0);
  const fee = o.delivery_fee;
  const discount_amount = Math.max(0, Math.round(subtotal + (fee || 0) - (o.total || 0)));
  return { ref: o.ref || `NK-${o.id}`, date: o.created_at ? (/Z|[+-]\d\d:?\d\d$/.test(o.created_at) ? o.created_at : o.created_at + 'Z') : null, customer_name: o.customer_name, customer_phone: o.customer_phone, delivery_area: o.delivery_area, address: o.address, items, subtotal, discount_amount, discount_label: o.promo_code || '', delivery_fee: fee, total: o.total || 0, paid: !!o.paid };
};
// Product page URLs: /product/<id>-<name-slug> (keep in sync with netlify/edge-functions/common/shared.js)
const slugify = (s) => String(s || '').toLowerCase().replace(/['’]/g, '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'item';
const productPath = (p) => `/product/${p.id}-${slugify(p.name)}`;
const productIdFromPath = () => { const m = window.location.pathname.match(/^\/product\/(\d+)/); return m ? parseInt(m[1], 10) : null; };

// Shrink a phone photo to max 900px JPEG before upload
const resizeImage = (file, max = 900) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Could not process image'))), 'image/jpeg', 0.85);
    URL.revokeObjectURL(img.src);
  };
  img.onerror = () => reject(new Error('That file is not an image'));
  img.src = URL.createObjectURL(file);
});

const uploadProductImage = async (file, key) => {
  const token = await getAccessToken();
  if (!token) throw new Error('Your admin login has expired. Please log in again.');
  const blob = await resizeImage(file, String(key).startsWith('hamper') ? 1600 : 900); // hamper posters need more detail
  const path = `${key}-${Date.now()}.jpg`;
  const res = await fetch(`${SUPABASE_URL}/storage/v1/object/${IMAGE_BUCKET}/${path}`, {
    method: 'POST',
    headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}`, 'Content-Type': 'image/jpeg', 'x-upsert': 'true' },
    body: blob,
  });
  if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || j.error || `Upload failed (${res.status})`); }
  return `${SUPABASE_URL}/storage/v1/object/public/${IMAGE_BUCKET}/${path}`;
};

const supabase = {
  from: (table) => ({
    select: (columns = '*') => supabaseFetch(`${SUPABASE_URL}/rest/v1/${table}?select=${columns}`),
    insert: (data) => supabaseFetch(`${SUPABASE_URL}/rest/v1/${table}`, { method: 'POST', body: JSON.stringify(data), headers: { Prefer: 'return=minimal' } }),
    update: (data, id) => mustChange(supabaseFetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, { method: 'PATCH', body: JSON.stringify(data), headers: { Prefer: 'return=representation' } })),
    delete: (id) => mustChange(supabaseFetch(`${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`, { method: 'DELETE', headers: { Prefer: 'return=representation' } })),
  }),
};

// ===== PDF INVOICE GENERATOR =====
const generateInvoice = (cartItems, total, finalTotal, promoCode) => {
  const invoiceNumber = 'INV-' + Date.now();
  const date = new Date().toLocaleDateString();
  const pdfContent = `
    <html><head><title>Invoice - ${invoiceNumber}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
      .header { text-align: center; margin-bottom: 30px; }
      .logo { font-size: 24px; font-weight: bold; color: #f97316; }
      table { width: 100%; border-collapse: collapse; margin: 20px 0; }
      th { background: #f97316; color: white; padding: 10px; text-align: left; }
      td { padding: 8px; border-bottom: 1px solid #ddd; }
      .total-row { font-weight: bold; font-size: 16px; }
      .footer { margin-top: 30px; padding-top: 20px; border-top: 2px solid #f97316; text-align: center; font-size: 12px; color: #666; }
    </style></head><body>
    <div class="header">
      <div class="logo">🍽️ NKATANNI FOOD MART</div>
      <p>Your Trusted Online Store for Foodstuff and Groceries</p>
    </div>
    <div class="invoice-details">
      <p><strong>Invoice #:</strong> ${invoiceNumber}</p>
      <p><strong>Date:</strong> ${date}</p>
    </div>
    <table>
      <thead><tr><th>Product</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
      <tbody>
        ${cartItems.map(item => `<tr><td>${item.name}</td><td>${item.qty}</td><td>₦${item.price.toLocaleString()}</td><td>₦${(item.price * item.qty).toLocaleString()}</td></tr>`).join('')}
      </tbody>
    </table>
    <div style="text-align: right; margin: 20px 0;">
      <p><strong>Subtotal:</strong> ₦${total.toLocaleString()}</p>
      ${promoCode ? `<p><strong>Promo Code:</strong> ${promoCode}</p>` : ''}
      <p class="total-row"><strong>Total:</strong> ₦${Math.floor(finalTotal).toLocaleString()}</p>
    </div>
    <div class="footer">
      <p>📍 Graceland Shopping Mall, Shop 117, Irete, Owerri | 📍 Ritchie & Hills Plaza, Suite 03, Egbeada, Owerri</p>
      <p>📞 +2348037891888 | 💬 WhatsApp: +2347055921564</p>
      <p>Thank you for your order!</p>
    </div></body></html>
  `;
  const printWindow = window.open('', '', 'width=800,height=600');
  printWindow.document.write(pdfContent);
  printWindow.document.close();
  printWindow.print();
};

// ===== MAIN APP COMPONENT =====
// ===== Reusable admin editor for small catalogues (Specials, Hampers) =====
const fieldToText = (f, v) => (f.type === 'list' ? (Array.isArray(v) ? v.join('\n') : (v || '')) : (v ?? ''));
const textToField = (f, t) => {
  if (f.type === 'number') { const n = parseInt(t, 10); return Number.isNaN(n) ? null : n; }
  if (f.type === 'list') return String(t).split('\n').map(x => x.trim()).filter(Boolean);
  return String(t).trim();
};
const AdminItemEditor = ({ title, addLabel, items, fields, onAdd, onSave, onDelete, onToggle, onUpload, uploadingKey, uploadPrefix, noPhoto, hint, header }) => {
  const [edits, setEdits] = useState({});
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const convert = (obj) => Object.fromEntries(Object.entries(obj).map(([k, t]) => [k, textToField(fields.find(f => f.key === k), t)]));
  const missing = (obj) => fields.filter(f => f.required).find(f => { const v = obj[f.key]; return v === null || v === '' || v === undefined || (Array.isArray(v) && v.length === 0); });
  const renderInput = (f, value, onChange) => (
    f.type === 'textarea' || f.type === 'list'
      ? <textarea rows={f.type === 'list' ? 4 : 2} placeholder={f.type === 'list' ? `${f.label} (one per line)` : f.label} value={value} onChange={(e) => onChange(e.target.value)} />
      : <input type={f.type === 'number' ? 'number' : 'text'} min="0" placeholder={f.label} value={value} onChange={(e) => onChange(e.target.value)} />
  );
  const add = async () => {
    const values = convert(Object.fromEntries(fields.map(f => [f.key, draft[f.key] ?? ''])));
    const m = missing(values); if (m) { alert(`Enter ${m.label}`); return; }
    setBusy(true); const ok = await onAdd(values); setBusy(false);
    if (ok) setDraft({});
  };
  const save = async (item) => {
    const changes = convert(edits[item.id] || {});
    const m = missing({ ...item, ...changes }); if (m) { alert(`Enter ${m.label}`); return; }
    const ok = await onSave(item, changes);
    if (ok) setEdits(prev => { const n = { ...prev }; delete n[item.id]; return n; });
  };
  return (
    <div className="admin-content">
      {title && <h2>{title}</h2>}
      {header}
      {hint && <p className="admin-hint admin-hint-top">{hint}</p>}
      <div className="form-section item-add">
        <h3>{addLabel}</h3>
        <div className="item-fields">
          {fields.map(f => <label key={f.key} className={`item-field f-${f.type || 'text'}`}><span>{f.label}</span>{renderInput(f, draft[f.key] ?? '', (v) => setDraft(prev => ({ ...prev, [f.key]: v })))}</label>)}
        </div>
        <button className="btn-primary" onClick={add} disabled={busy}>{busy ? 'Saving…' : addLabel}</button>
        {!noPhoto && <p className="admin-hint">Add the photo after saving, using the photo box on the item.</p>}
      </div>
      {items.length === 0 && <p className="admin-count">Nothing here yet.</p>}
      <div className="item-list">
        {items.map(item => {
          const e = edits[item.id] || {};
          const dirty = Object.keys(e).length > 0;
          const on = item.active !== false;
          return (
            <div key={item.id} className={`item-card ${on ? '' : 'item-off'}`}>
              {!noPhoto && <label className="photo-cell item-photo" title="Change photo">
                <img src={imgSrc(item.image)} alt="" />
                <span>{uploadingKey === `${uploadPrefix}-${item.id}` ? 'Uploading…' : (item.image ? 'Change photo' : 'Add photo')}</span>
                <input type="file" accept="image/*" hidden onChange={(ev) => { onUpload(item, ev.target.files[0]); ev.target.value = ''; }} />
              </label>}
              <div className="item-fields">
                {fields.map(f => <label key={f.key} className={`item-field f-${f.type || 'text'}`}><span>{f.label}</span>{renderInput(f, f.key in e ? e[f.key] : fieldToText(f, item[f.key]), (v) => setEdits(prev => ({ ...prev, [item.id]: { ...prev[item.id], [f.key]: v } })))}</label>)}
              </div>
              <div className="item-actions">
                <label className="stock-toggle"><input type="checkbox" checked={on} onChange={() => onToggle(item)} /> {on ? 'Showing' : 'Hidden'}</label>
                <button className="btn-row-save" disabled={!dirty} onClick={() => save(item)}>Save</button>
                <button className="btn-delete" onClick={() => onDelete(item)}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function App() {
  const [page, setPage] = useState(() => {
    // Direct links like nkatannifoodmart.com/hampers (used by the WhatsApp catalogue)
    if (typeof window !== 'undefined' && /^\/(hampers|combos)\/?$/.test(window.location.pathname)) return 'hampers';
    if (typeof window !== 'undefined' && /^\/faq\/?$/.test(window.location.pathname)) return 'faq';
    // On mobile, land directly on Shop; on desktop, keep Home
    if (typeof window !== 'undefined' && window.innerWidth < 768) return 'shop';
    return 'home';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('All Products');
  const [adminLoggedIn, setAdminLoggedIn] = useState(() => !!getSession());
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminBusy, setAdminBusy] = useState(false);
  const [productEdits, setProductEdits] = useState({});
  const [adminSearch, setAdminSearch] = useState('');
  const [adminNoPhoto, setAdminNoPhoto] = useState(false);
  const [adminWaOnly, setAdminWaOnly] = useState(false);
  const [adminHiddenOnly, setAdminHiddenOnly] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [invBusy, setInvBusy] = useState(false);
  const [newInv, setNewInv] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('active');
  const [openOrderId, setOpenOrderId] = useState(null);
  const [orderEdits, setOrderEdits] = useState({});
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const [variantProductId, setVariantProductId] = useState('');
  const [variantSearch, setVariantSearch] = useState('');
  const [categoryEdits, setCategoryEdits] = useState({});
  const [spSearch, setSpSearch] = useState('');
  const [spProductId, setSpProductId] = useState('');
  const [spPrice, setSpPrice] = useState('');
  const [adminTab, setAdminTab] = useState('products');
  const [selectedVariant, setSelectedVariant] = useState({});
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactMessage, setContactMessage] = useState('');
  
  // NEW: Product detail modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalQty, setModalQty] = useState(1);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    products: [],
    variants: [],
    recipes: [],
    hampers: [],
    dailySpecials: [],
    categories: ['All Products'],
    referralCodes: [],
    campaigns: [],
    categoryRows: [],
  });

  const [formData, setFormData] = useState({
    product: { name: '', category: '', price: '' },
    variant: { product_id: '', variant_name: '', price: '', size: '' },
    recipe: { name: '', ingredients: '', time: '', difficulty: '', image: '', description: '' },
    hamper: { name: '', price: '', items: '', image: '', desc: '' },
    special: { name: '', originalPrice: '', salePrice: '', image: '' },
    category: '',
    referral: { code: '', discount: '' },
    campaign: { name: '', discount: '' },
  });

  const [cart, setCart] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [customer, setCustomer] = useState(() => ({ name: '', phone: '', zone: '', address: '', ...loadCustomer() }));
  const setCust = (field, value) => setCustomer(prev => { const next = { ...prev, [field]: value }; try { localStorage.setItem('nk_customer', JSON.stringify(next)); } catch (e) {} return next; });

  useEffect(() => {
    loadAllData();
    const saved = localStorage.getItem('nkatanni_cart');
    if (saved) {
      try { setCart(JSON.parse(saved)); } catch (e) { console.log('Cart load error:', e); }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('nkatanni_cart', JSON.stringify(cart));
  }, [cart]);

  // GA4 + Meta page tracking on virtual route change
  const fbFirstView = useRef(true);
  useEffect(() => {
    if (fbFirstView.current) fbFirstView.current = false; else if (page !== 'admin') fb('PageView');
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: `Nkatanni - ${page.charAt(0).toUpperCase() + page.slice(1)}`,
        page_path: `/${page === 'home' ? '' : page}`,
        page_location: window.location.href,
      });
    }
    // Update document title per page for better SEO + analytics
    const titles = {
      home: 'Nkatanni Food Mart — Authentic Nigerian Groceries Online',
      shop: 'Shop All Products — Nkatanni Food Mart',
      recipes: 'African Recipes — Nkatanni Food Mart',
      hampers: 'Food Hampers & Bundles — Nkatanni Food Mart',
      export: 'Export & Diaspora Shipping — Nkatanni Food Mart',
      about: 'About Us — Nkatanni Food Mart',
      contact: 'Contact Us — Nkatanni Food Mart',
      faq: 'FAQs — Nkatanni Food Mart',
      cart: 'Your Cart — Nkatanni Food Mart',
      admin: 'Admin — Nkatanni Food Mart',
    };
    if (titles[page]) document.title = titles[page];
    // FAQ page: own URL + FAQPage structured data for Google
    const path = window.location.pathname;
    if (page === 'faq' && !/^\/faq\/?$/.test(path)) window.history.pushState(null, '', '/faq');
    if (page !== 'faq' && /^\/faq\/?$/.test(path)) window.history.replaceState(null, '', '/');
    const old = document.getElementById('faq-jsonld'); if (old) old.remove();
    if (page === 'faq') {
      const el = document.createElement('script'); el.type = 'application/ld+json'; el.id = 'faq-jsonld';
      el.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) });
      document.head.appendChild(el);
    }
  }, [page]);

  // GA4 e-commerce event when WhatsApp checkout fires
  const trackCheckout = (items, total) => {
    const fbParams = { content_ids: items.map(i => nkId(i.id)), content_type: 'product', num_items: items.reduce((n, i) => n + i.qty, 0), value: total, currency: 'NGN' };
    fb('InitiateCheckout', fbParams);
    fb('Lead', fbParams);
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'begin_checkout', {
      currency: 'NGN',
      value: total,
      items: items.map(i => ({ item_name: i.name, quantity: i.qty, price: i.price })),
    });
  };

  const loadAllData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const tables = ['products', 'product_variants', 'recipes', 'hampers', 'daily_specials', 'categories', 'referral_codes', 'campaigns'];
      const results = await Promise.all(tables.map(t => supabase.from(t).select()));
      const [products, variants, recipes, hampers, specials, categories, codes, campaigns] = results.map(r => r.data || []);
      setData({
        products: (products || []).filter(p => p.category !== '_deleted_dupe'),
        variants: variants || [],
        recipes: recipes || [],
        hampers: hampers || [],
        dailySpecials: specials || [],
        categories: ['All Products', ...(categories || []).map(c => c.name).filter(n => n && n !== 'All Products' && !n.startsWith('__OLD__') && !n.startsWith('_deleted'))],
        categoryRows: (categories || []).filter(c => c.name && !c.name.startsWith('__OLD__') && !c.name.startsWith('_deleted') && c.name !== 'All Products'),
        referralCodes: codes || [],
        campaigns: campaigns || [],
      });
    } catch (err) { console.error('Error loading data:', err); }
    finally { setLoading(false); }
  };

  const handleAdd = async (table, formValues, resetFn) => {
    if (!formValues || Object.values(formValues).some(v => v === '' || v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v)))) { alert('Fill all fields'); return; }
    const { error } = await supabase.from(table).insert(formValues);
    if (error) { alert(`Could not save: ${error.message}`); return; }
    resetFn();
    alert('Saved!');
    loadAllData(true);
  };

  const handleDelete = async (table, id, label = 'this item') => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    const { error } = await supabase.from(table).delete(id);
    if (error) { alert(`Could not delete: ${error.message}`); return; }
    loadAllData(true);
  };

  /* eslint-disable no-unused-vars */
  const handleToggle = async (table, id, currentStatus) => {
    const { error } = await supabase.from(table).update({ active: !currentStatus }, id);
    if (error) { alert(`Could not update: ${error.message}`); return; }
    loadAllData(true);
  };

  const addProduct = () => handleAdd('products', { name: formData.product.name, category: formData.product.category, price: parseInt(formData.product.price), rating: 4.5, reviews: 0 }, () => setFormData({ ...formData, product: { name: '', category: '', price: '' } }));
  const addVariant = () => handleAdd('product_variants', { product_id: parseInt(formData.variant.product_id), variant_name: formData.variant.variant_name, price: parseInt(formData.variant.price), size: formData.variant.size, quantity_in_stock: 100, active: true }, () => setFormData({ ...formData, variant: { product_id: '', variant_name: '', price: '', size: '' } }));
  const addRecipe = () => handleAdd('recipes', { name: formData.recipe.name, ingredients: formData.recipe.ingredients.split(',').map(i => i.trim()), time: formData.recipe.time, difficulty: formData.recipe.difficulty, image: formData.recipe.image, description: formData.recipe.description }, () => setFormData({ ...formData, recipe: { name: '', ingredients: '', time: '', difficulty: '', image: '', description: '' } }));
  const addHamper = () => handleAdd('hampers', { name: formData.hamper.name, price: parseInt(formData.hamper.price), items: formData.hamper.items.split(',').map(i => i.trim()), image: formData.hamper.image, desc: formData.hamper.desc }, () => setFormData({ ...formData, hamper: { name: '', price: '', items: '', image: '', desc: '' } }));
  const addSpecial = () => handleAdd('daily_specials', { name: formData.special.name, original_price: parseInt(formData.special.originalPrice), sale_price: parseInt(formData.special.salePrice), discount: Math.round((1 - parseInt(formData.special.salePrice) / parseInt(formData.special.originalPrice)) * 100), image: formData.special.image, active: true }, () => setFormData({ ...formData, special: { name: '', originalPrice: '', salePrice: '', image: '' } }));
  const addCategory = () => { if (!formData.category.trim() || data.categories.includes(formData.category)) { alert(data.categories.includes(formData.category) ? 'Category exists' : 'Enter name'); return; } handleAdd('categories', { name: formData.category }, () => setFormData({ ...formData, category: '' })); };
  const addReferral = () => handleAdd('referral_codes', { code: formData.referral.code, discount: parseInt(formData.referral.discount), uses: 0, active: true }, () => setFormData({ ...formData, referral: { code: '', discount: '' } }));
  const addCampaign = () => handleAdd('campaigns', { name: formData.campaign.name, discount: parseInt(formData.campaign.discount), active: true }, () => setFormData({ ...formData, campaign: { name: '', discount: '' } }));
  /* eslint-enable no-unused-vars */

  const editProduct = (id, field, value) => setProductEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  const saveProduct = async (p) => {
    const changes = { ...productEdits[p.id] };
    if ('name' in changes) { changes.name = changes.name.trim(); if (!changes.name) { alert('Name cannot be empty'); return; } }
    if ('price' in changes) { const n = parseInt(changes.price, 10); if (Number.isNaN(n) || n < 0) { alert('Enter a valid price'); return; } changes.price = n; }
    if ('description' in changes) changes.description = changes.description.trim() || null;
    const { error } = await supabase.from('products').update(changes, p.id);
    if (error) { alert(`Could not save: ${error.message}`); return; }
    setProductEdits(prev => { const n = { ...prev }; delete n[p.id]; return n; });
    setData(prev => ({ ...prev, products: prev.products.map(x => (x.id === p.id ? { ...x, ...changes } : x)) }));
  };
  const toggleWhatsapp = async (p) => {
    const next = !p.on_whatsapp;
    const { error } = await supabase.from('products').update({ on_whatsapp: next }, p.id);
    if (error) { alert(`Could not update: ${error.message}`); return; }
    setData(prev => ({ ...prev, products: prev.products.map(x => (x.id === p.id ? { ...x, on_whatsapp: next } : x)) }));
  };
  const toggleHidden = async (p) => {
    const next = !p.hidden;
    const changes = next ? { hidden: true, on_whatsapp: false } : { hidden: false };
    const { error } = await supabase.from('products').update(changes, p.id);
    if (error) { alert(`Could not update: ${error.message}`); return; }
    setData(prev => ({ ...prev, products: prev.products.map(x => (x.id === p.id ? { ...x, ...changes } : x)) }));
  };
  const toggleStock = async (p) => {
    const next = isOutOfStock(p);
    const { error } = await supabase.from('products').update({ in_stock: next }, p.id);
    if (error) { alert(`Could not update stock: ${error.message}`); return; }
    setData(prev => ({ ...prev, products: prev.products.map(x => (x.id === p.id ? { ...x, in_stock: next } : x)) }));
  };
  const handlePhotoUpload = async (p, file) => {
    if (!file) return;
    setUploadingId(p.id);
    try {
      const url = await uploadProductImage(file, `product-${p.id}`);
      const { error } = await supabase.from('products').update({ image: url }, p.id);
      if (error) throw error;
      setData(prev => ({ ...prev, products: prev.products.map(x => (x.id === p.id ? { ...x, image: url } : x)) }));
    } catch (e) {
      alert(`Photo upload failed: ${e.message}`);
    } finally {
      setUploadingId(null);
    }
  };

  // Specials & hampers
  const discountOf = (o, s2) => (o > 0 && s2 >= 0 && s2 < o ? Math.round((1 - s2 / o) * 100) : 0);
  const patchLocal = (dataKey, id, changes) => setData(prev => ({ ...prev, [dataKey]: prev[dataKey].map(x => (x.id === id ? { ...x, ...changes } : x)) }));
  const makeCatalogHandlers = (table, dataKey, prefix, prep = (c) => c) => ({
    onAdd: async (values) => {
      const { error } = await supabase.from(table).insert(prep({ ...values, active: true }, null));
      if (error) { alert(`Could not save: ${error.message}`); return false; }
      await loadAllData(true); return true;
    },
    onSave: async (item, changes) => {
      const full = prep(changes, item);
      const { error } = await supabase.from(table).update(full, item.id);
      if (error) { alert(`Could not save: ${error.message}`); return false; }
      patchLocal(dataKey, item.id, full); return true;
    },
    onToggle: async (item) => {
      const next = item.active === false;
      const { error } = await supabase.from(table).update({ active: next }, item.id);
      if (error) { alert(`Could not update: ${error.message}`); return; }
      patchLocal(dataKey, item.id, { active: next });
    },
    onDelete: async (item) => {
      if (!window.confirm(`Delete "${item.name || item.code || item.variant_name}"? This cannot be undone.`)) return;
      const { error } = await supabase.from(table).delete(item.id);
      if (error) { alert(`Could not delete: ${error.message}`); return; }
      setData(prev => ({ ...prev, [dataKey]: prev[dataKey].filter(x => x.id !== item.id) }));
    },
    onUpload: async (item, file) => {
      if (!file) return;
      const key = `${prefix}-${item.id}`;
      setUploadingId(key);
      try {
        const url = await uploadProductImage(file, key);
        const { error } = await supabase.from(table).update({ image: url }, item.id);
        if (error) throw error;
        patchLocal(dataKey, item.id, { image: url });
      } catch (e) { alert(`Photo upload failed: ${e.message}`); } finally { setUploadingId(null); }
    },
  });
  const specialHandlers = makeCatalogHandlers('daily_specials', 'dailySpecials', 'special', (c, item) => {
    const o = 'original_price' in c ? c.original_price : item && item.original_price;
    const sp = 'sale_price' in c ? c.sale_price : item && item.sale_price;
    return ('original_price' in c || 'sale_price' in c) ? { ...c, discount: discountOf(o, sp) } : c;
  });
  const hamperHandlers = makeCatalogHandlers('hampers', 'hampers', 'hamper');
  const recipeHandlers = makeCatalogHandlers('recipes', 'recipes', 'recipe');
  const pctOk = (c) => { if ('discount' in c && (c.discount < 1 || c.discount > 90)) throw new Error('Discount must be between 1 and 90%'); return c; };
  const guard = (h) => ({ ...h, onAdd: async (v) => { try { return await h.onAdd(v); } catch (e) { alert(e.message); return false; } }, onSave: async (i, c) => { try { return await h.onSave(i, c); } catch (e) { alert(e.message); return false; } } });
  const codeHandlers = guard(makeCatalogHandlers('referral_codes', 'referralCodes', 'code', (c) => pctOk('code' in c ? { ...c, code: String(c.code).toUpperCase().replace(/\s+/g, '') } : c)));
  const campaignHandlers = guard(makeCatalogHandlers('campaigns', 'campaigns', 'campaign', pctOk));
  const variantHandlers = makeCatalogHandlers('product_variants', 'variants', 'variant', (c, item) => (item ? c : { ...c, product_id: parseInt(variantProductId, 10), quantity_in_stock: 100 }));

  // Categories: rename updates every product in it; delete only when empty
  const productCountIn = (name) => data.products.filter(p => p.category === name).length;
  const renameCategory = async (row) => {
    const next = (categoryEdits[row.id] || '').trim();
    if (!next || next === row.name) return;
    if (data.categoryRows.some(c => c.name.toLowerCase() === next.toLowerCase() && c.id !== row.id)) { alert('A category with that name already exists'); return; }
    const n = productCountIn(row.name);
    if (!window.confirm(`Rename "${row.name}" to "${next}"? ${n} product${n !== 1 ? 's' : ''} will move with it.`)) return;
    const { error } = await supabase.from('categories').update({ name: next }, row.id);
    if (error) { alert(`Could not rename: ${error.message}`); return; }
    if (n > 0) {
      const res = await supabaseFetch(`${SUPABASE_URL}/rest/v1/products?category=eq.${encodeURIComponent(row.name)}`, { method: 'PATCH', body: JSON.stringify({ category: next }), headers: { Prefer: 'return=minimal' } });
      if (res.error) alert(`Category renamed, but products were not moved: ${res.error.message}`);
    }
    setCategoryEdits(prev => { const x = { ...prev }; delete x[row.id]; return x; });
    if (category === row.name) setCategory(next);
    await loadAllData(true);
  };
  const deleteCategory = async (row) => {
    const n = productCountIn(row.name);
    if (n > 0) { alert(`"${row.name}" still has ${n} product${n !== 1 ? 's' : ''}. Move them to another category first.`); return; }
    if (!window.confirm(`Delete the empty category "${row.name}"?`)) return;
    const { error } = await supabase.from('categories').delete(row.id);
    if (error) { alert(`Could not delete: ${error.message}`); return; }
    await loadAllData(true);
  };

  const getProductVariants = (productId) => data.variants.filter(v => v.product_id === productId && v.active);

  const addToCart = (product, selectedVariantId = null, qty = 1) => {
    if (isOutOfStock(product)) { alert(`${product.name} is out of stock`); return; }
    let cartItem = { ...product, qty };
    if (selectedVariantId) {
      const variant = data.variants.find(v => v.id === selectedVariantId);
      if (variant) {
        cartItem = { ...product, id: `${product.id}-${variant.id}`, name: `${product.name} - ${variant.variant_name}`, price: variant.price, variantId: variant.id, qty };
      }
    }
    const existing = cart.find(item => item.id === cartItem.id);
    const nextCart = existing ? cart.map(item => (item.id === cartItem.id ? { ...item, qty: item.qty + qty } : item)) : [...cart, cartItem];
    setCart(nextCart);
    fb('AddToCart', { content_ids: [nkId(product.id)], content_type: 'product', content_name: cartItem.name, value: cartItem.price * qty, currency: 'NGN' });
    const newTotal = nextCart.reduce((sum, item) => sum + item.price * item.qty, 0);
    setToast({ name: cartItem.name, qty, total: newTotal });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  const removeFromCart = (id) => setCart(cart.filter(item => item.id !== id));
  const updateQty = (id, qty) => { if (qty <= 0) removeFromCart(id); else setCart(cart.map(item => item.id === id ? {...item, qty} : item)); };

  const checkout = async () => {
    if (cart.length === 0) { alert('Your cart is empty!'); return; }
    const name = customer.name.trim();
    const phoneDigits = customer.phone.replace(/\D/g, '');
    if (!name) { alert('Please enter your name'); return; }
    if (phoneDigits.length < 10) { alert('Please enter a valid phone number'); return; }
    if (!zone) { alert('Please choose your delivery area'); return; }
    if (zone.id !== 'pickup' && !customer.address.trim()) { alert('Please enter your delivery address'); return; }
    trackCheckout(cart, Math.floor(grandTotal));
    const orderRef = makeRef();
    const cartText = cart.map(i => `${i.name} x${i.qty} = ₦${(i.price * i.qty).toLocaleString()}`).join('\n');
    const deliveryText = deliveryFee === null ? 'To be quoted' : deliveryFee === 0 ? (zone.id === 'pickup' ? 'Pick up (free)' : 'FREE') : naira(deliveryFee);
    const message = `Hello Nkatanni,\n\nI would like to place an order (Ref: ${orderRef}):\n\n${cartText}\n\nSubtotal: ₦${total.toLocaleString()}${discount > 0 ? `\nDiscount: ${discount}% (${discountLabel})` : ''}\nDelivery: ${deliveryText}\nTotal: ₦${Math.floor(grandTotal).toLocaleString()}${deliveryFee === null ? ' + delivery' : ''}\n\nName: ${name}\nPhone: ${customer.phone.trim()}\nDelivery area: ${zone.label}${zone.id !== 'pickup' ? `\nAddress: ${customer.address.trim()}` : ''}${promoCode ? `\nPromo Code: ${promoCode}` : ''}\n\nInvoice: ${orderRef}\nPay to: ${BANK.bank} · ${BANK.number} · ${BANK.name}\n(Payment confirms the order)`;
    const orderRow = { ref: orderRef, customer_name: name, customer_phone: customer.phone.trim(), delivery_area: zone.label, address: zone.id === 'pickup' ? null : customer.address.trim(), delivery_fee: deliveryFee, items: JSON.stringify(cart), subtotal: total, total: Math.floor(grandTotal), promo_code: promoCode || null, status: 'pending' };
    try {
      await supabase.from('orders').insert(orderRow);
    } catch (err) { console.error('Error saving order:', err); }
    window.open(`https://wa.me/2347055921564?text=${encodeURIComponent(message)}`, '_blank');
    setLastInvoice({ ...orderToInvoice({ ...orderRow, created_at: new Date().toISOString() }), discount_amount: Math.max(0, Math.round(total - finalTotal)), discount_label: discountLabel });
    setCart([]); setPromoCode('');
  };

  // Storefront sees only products not hidden in admin; admin still sees everything
  const shopProducts = data.products.filter(p => !p.hidden);
  const shopCategories = data.categories.filter(c => c === 'All Products' || shopProducts.some(p => p.category === c));
  // Apply category filter + search, then sort Nkatanni Brand items first in All Products view
  const _searchMatch = (p) => p.name.toLowerCase().includes(searchQuery.toLowerCase());
  const _preFiltered = category === 'All Products'
    ? shopProducts.filter(_searchMatch)
    : category === 'Nkatanni Brand'
      ? shopProducts.filter(p => isNkatanniBrand(p) && _searchMatch(p))
      : shopProducts.filter(p => p.category === category && _searchMatch(p));
  // In "All Products", own-brand first; in Nkatanni Brand, keep order
  const filtered = category === 'All Products'
    ? [..._preFiltered.filter(isNkatanniBrand), ..._preFiltered.filter(p => !isNkatanniBrand(p))]
    : _preFiltered;
  // Extended category list with Nkatanni Brand as the first filter
  const sidebarCategories = ['Nkatanni Brand', ...shopCategories];

  const total = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const activePromoCode = data.referralCodes.find(c => c.code === promoCode && c.active !== false);
  const activeCampaign = data.campaigns.filter(c => c.active !== false && c.discount > 0).sort((a, b) => b.discount - a.discount)[0] || null;
  const publicCodes = data.referralCodes.filter(c => c.active !== false && c.discount > 0);
  const codeDiscount = activePromoCode ? activePromoCode.discount : 0;
  const campaignDiscount = activeCampaign ? activeCampaign.discount : 0;
  // Best single discount applies (they don't stack)
  const discount = Math.max(codeDiscount, campaignDiscount);
  const discountLabel = discount === 0 ? '' : (codeDiscount >= campaignDiscount ? `Code ${activePromoCode.code}` : activeCampaign.name);
  const finalTotal = discount > 0 ? total * (1 - discount / 100) : total;
  const zone = DELIVERY_ZONES.find(z => z.id === customer.zone) || null;
  const deliveryFee = !zone ? (total >= FREE_DELIVERY_MIN ? 0 : DELIVERY_FEE) : zone.fee === null ? null : (zone.free && total >= FREE_DELIVERY_MIN ? 0 : zone.fee);
  const grandTotal = finalTotal + (deliveryFee || 0);
  const activeSpecials = data.dailySpecials.filter(s => s.active !== false);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) { setContactMessage('Please fill all fields'); return; }
    const whatsappMsg = `Name: ${contactForm.name}%0AEmail: ${contactForm.email}%0APhone: ${contactForm.phone}%0A%0AMessage: ${contactForm.message}`;
    window.open(`https://wa.me/2347055921564?text=${whatsappMsg}`, '_blank');
    setContactForm({ name: '', email: '', phone: '', message: '' });
    setContactMessage('Message sent to WhatsApp!');
    setTimeout(() => setContactMessage(''), 3000);
  };

  const handleAdminLogin = async () => {
    if (!adminEmail.trim() || !adminPassword) { alert('Enter your admin email and password'); return; }
    setAdminBusy(true);
    try {
      await adminSignIn(adminEmail.trim(), adminPassword);
      setAdminLoggedIn(true);
      setAdminPassword('');
    } catch (e) {
      alert(e.message === 'Invalid login credentials' ? 'Wrong email or password' : e.message);
    } finally {
      setAdminBusy(false);
    }
  };
  const loadOrders = async () => {
    setOrdersLoading(true);
    const { data: rows, error } = await supabase.from('orders').select('*');
    setOrdersLoading(false);
    if (error) { alert(`Could not load orders: ${error.message}`); return; }
    setOrders((rows || []).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
  };
  const updateOrder = async (o, changes) => {
    const { error } = await supabase.from('orders').update(changes, o.id);
    if (error) { alert(`Could not update order: ${error.message}`); return false; }
    setOrders(prev => prev.map(x => (x.id === o.id ? { ...x, ...changes } : x)));
    return true;
  };
  const saveOrderCustomer = async (o) => {
    const ok = await updateOrder(o, orderEdits[o.id] || {});
    if (ok) setOrderEdits(prev => { const n = { ...prev }; delete n[o.id]; return n; });
  };
  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.ref || '#' + o.id}? This cannot be undone.`)) return;
    const { error } = await supabase.from('orders').delete(o.id);
    if (error) { alert(`Could not delete: ${error.message}`); return; }
    setOrders(prev => prev.filter(x => x.id !== o.id));
  };

  const handleAdminLogout = () => { adminSignOut(); setAdminLoggedIn(false); setPage('home'); };

  // Product detail: each product has its own shareable URL (/product/<id>-<name>)
  const showProduct = (p) => {
    setSelectedProduct(p); setModalQty(1);
    document.title = `${p.name} — Nkatanni Food Mart`;
    fb('ViewContent', { content_ids: [nkId(p.id)], content_type: 'product', content_name: p.name, content_category: p.category, value: p.price, currency: 'NGN' });
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_title: p.name, page_path: productPath(p), page_location: window.location.href });
      window.gtag('event', 'view_item', { currency: 'NGN', value: p.price, items: [{ item_id: `NK-${String(p.id).padStart(4, '0')}`, item_name: p.name, item_category: p.category, price: p.price }] });
    }
  };
  const openProductDetail = (p) => {
    if (window.location.pathname !== productPath(p)) window.history.pushState({ nkProduct: p.id }, '', productPath(p));
    showProduct(p);
  };
  const closeProductDetail = () => {
    setSelectedProduct(null);
    if (productIdFromPath()) {
      if (window.history.state && window.history.state.nkProduct) window.history.back();
      else window.history.replaceState(null, '', '/');
    }
    document.title = 'Nkatanni Food Mart — Authentic Nigerian Groceries Online';
  };
  const shareProduct = (p) => {
    const url = `${window.location.origin}${productPath(p)}`;
    const text = `${p.name} — ₦${Number(p.price || 0).toLocaleString()} at Nkatanni Food Mart`;
    if (typeof window.gtag === 'function') window.gtag('event', 'share', { method: navigator.share ? 'native' : 'whatsapp', item_id: `NK-${String(p.id).padStart(4, '0')}` });
    if (navigator.share) { navigator.share({ title: p.name, text, url }).catch(() => {}); return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, '_blank');
  };

  // Open the product from the address bar (shared links, back/forward buttons)
  const productsLoaded = data.products.length > 0;
  useEffect(() => {
    if (!productsLoaded) return undefined;
    const sync = () => {
      const id = productIdFromPath();
      if (!id) { setSelectedProduct(null); return; }
      const p = data.products.find(x => x.id === id && !x.hidden);
      if (p) showProduct(p); else window.history.replaceState(null, '', '/');
    };
    sync();
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsLoaded]);

  if (loading) {
    return <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px'}}>Loading Nkatanni...</div>;
  }

  return (
    <div className="app">
      <header className="header">
        {total >= FREE_DELIVERY_MIN ? (
          <div className="top-bar top-bar-won">🎉 You've unlocked <strong>FREE delivery</strong> on this order!</div>
        ) : total > 0 ? (
          <button className="top-bar top-bar-cta" onClick={() => setPage('cart')}>
            🚚 Add <strong>{naira(FREE_DELIVERY_MIN - total)}</strong> more for <strong>FREE delivery</strong>
            <span className="top-bar-meter"><span style={{ width: `${Math.min(100, (total / FREE_DELIVERY_MIN) * 100)}%` }} /></span>
          </button>
        ) : (
          <button className="top-bar top-bar-cta" onClick={() => { setPage('shop'); setCategory('All Products'); }}>
            🚚 <strong>FREE delivery</strong> on orders of ₦100,000+ · <u>Start shopping</u>
          </button>
        )}
        <div className="main-header">
          <div className="container header-flex">
            <img src="/assets/NKATA NNI LOGO var2b.jpg" alt="Nkatanni Food Mart" className="logo" onClick={() => setPage('home')} style={{cursor: 'pointer'}} onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'inline'; }} />
            <span className="logo-text" style={{display: 'none'}} onClick={() => setPage('home')}>nkàtànni</span>
            <nav className={`nav ${mobileMenuOpen ? 'open' : ''}`}>
              <button onClick={() => {setPage('home'); setMobileMenuOpen(false);}} className={page === 'home' ? 'active' : ''}>Home</button>
              <button onClick={() => {setPage('shop'); setCategory('All Products'); setMobileMenuOpen(false);}} className={page === 'shop' && category === 'All Products' ? 'active' : ''}>Shop</button>
              <button onClick={() => {setPage('shop'); setCategory('Nkatanni Brand'); setMobileMenuOpen(false);}} className={`nav-brand ${page === 'shop' && category === 'Nkatanni Brand' ? 'active' : ''}`}>⭐ Our Brand</button>
              {['recipes', 'hampers', 'export', 'about', 'contact', 'faq'].map(p => (
                <button key={p} onClick={() => {setPage(p); setMobileMenuOpen(false);}} className={page === p ? 'active' : ''}>
                  {p === 'faq' ? 'FAQ' : p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </nav>
            <div className="header-actions">
              <button className="menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <button className="cart-btn" onClick={() => setPage('cart')}>
                <ShoppingCart size={20} />
                {cart.length > 0 && <span className="badge">{cart.length}</span>}
              </button>
              {adminLoggedIn && (
                <button className="admin-btn" onClick={() => {setPage('admin'); setAdminTab('products');}} title="Admin Panel" style={{fontSize: '18px', padding: '4px 8px', border: '1px solid #f97316', background: '#f97316', color: 'white', borderRadius: '4px', cursor: 'pointer'}}>
                  ⚙️
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      {(
        <div className="offer-bar">
          {activeCampaign && <span>📢 <strong>{activeCampaign.name}</strong> — {activeCampaign.discount}% off everything, applied at checkout</span>}
          {publicCodes.slice(0, 2).map(c => (
            <span key={c.id}>🎟️ Use code <button className="offer-code" onClick={() => { setPromoCode(c.code); try { navigator.clipboard.writeText(c.code); } catch (e) {} setPage('cart'); }}>{c.code}</button> for {c.discount}% off</span>
          ))}
          <button className="offer-channel" onClick={() => joinChannel('offer_bar')}>📲 Get promos first — join our WhatsApp Channel</button>
        </div>
      )}
      {lastInvoice && (
        <div className="modal-overlay" onClick={() => setLastInvoice(null)}>
          <div className="channel-modal invoice-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setLastInvoice(null)}><X size={22} /></button>
            <div className="channel-icon">🧾</div>
            <h3>Order {lastInvoice.ref} sent!</h3>
            <p>Please pay <strong>₦{Number(lastInvoice.total || 0).toLocaleString()}</strong>{lastInvoice.delivery_fee == null ? ' (+ delivery, to be quoted)' : ''} to confirm your order:</p>
            <div className="bank-box">
              <div><span>Bank</span><strong>{BANK.bank}</strong></div>
              <div><span>Account name</span><strong>{BANK.name}</strong></div>
              <div><span>Account no.</span><strong className="acct">{BANK.number}</strong>
                <button className="link-btn" onClick={(e) => { const b = e.currentTarget; try { navigator.clipboard.writeText(BANK.number); b.textContent = 'Copied ✓'; } catch (err) {} }}>Copy</button></div>
              <div><span>Reference</span><strong>{lastInvoice.ref}</strong></div>
            </div>
            <p className="inv-note">{INVOICE_TERMS}</p>
            <button className="btn-primary" disabled={invBusy} onClick={async () => { setInvBusy(true); try { await downloadInvoice(lastInvoice); if (window.gtag) window.gtag('event', 'invoice_download', { placement: 'checkout' }); } catch (e) { alert(e.message); } finally { setInvBusy(false); } }}>{invBusy ? 'Preparing…' : '⬇ Download invoice (PDF)'}</button>
            <button className="link-btn" onClick={() => setLastInvoice(null)}>Close</button>
          </div>
        </div>
      )}
      {zoomImg && (
        <div className="lightbox" onClick={() => setZoomImg(null)}>
          <button className="lightbox-close" onClick={() => setZoomImg(null)} aria-label="Close"><X size={28} /></button>
          <img src={zoomImg.src} alt={zoomImg.alt} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      {toast && (
        <div className="cart-toast" role="status">
          <div className="cart-toast-main">
            <span>✓ Added {toast.qty > 1 ? `${toast.qty} × ` : ''}<strong>{toast.name}</strong></span>
            <button onClick={() => { setToast(null); setPage('cart'); }}>View cart</button>
          </div>
          <div className="cart-toast-fd">
            {toast.total >= FREE_DELIVERY_MIN
              ? <>🎉 FREE delivery unlocked!</>
              : <>🚚 {naira(FREE_DELIVERY_MIN - toast.total)} more for FREE delivery</>}
            <span className="fd-meter"><span style={{ width: `${Math.min(100, (toast.total / FREE_DELIVERY_MIN) * 100)}%` }} /></span>
          </div>
        </div>
      )}

      <main className="main">
        {page === 'home' && (
          <>
            <section className="hero">
              <div className="hero-content">
                <h1>Authentic Nigerian Groceries, Delivered</h1>
                <p>Browse 300+ products. Order on WhatsApp. We deliver nationwide and worldwide.</p>
                <div className="hero-ctas">
                  <button className="btn-hero" onClick={() => setPage('shop')}>Shop Now</button>
                  <button className="btn-hero-outline" onClick={() => window.open('https://wa.me/2347055921564', '_blank')}>💬 Order on WhatsApp</button>
                </div>
              </div>
            </section>
            <section className="trust-strip">
              <div className="container trust-grid">
                <div className="trust-item"><span className="trust-icon">🚚</span><div><strong>Free Delivery</strong><p>On orders over ₦100,000</p></div></div>
                <div className="trust-item"><span className="trust-icon">💬</span><div><strong>WhatsApp Orders</strong><p>Quick & easy checkout</p></div></div>
                <div className="trust-item"><span className="trust-icon">🏪</span><div><strong>2 Stores in Owerri</strong><p>Graceland & Ritchie Hills</p></div></div>
                <div className="trust-item"><span className="trust-icon">🌍</span><div><strong>Worldwide Shipping</strong><p>To 50+ countries</p></div></div>
              </div>
            </section>
            <section className="category-tiles">
              <div className="container">
                <h2>Shop by Category</h2>
                <div className="cat-grid">
                  {['Nkatanni Brand', ...shopCategories.filter(c => c !== 'All Products')].slice(0, 9).map(cat => (
                    <button key={cat} className={`cat-tile ${cat === 'Nkatanni Brand' ? 'cat-tile-brand' : ''}`} onClick={() => {setPage('shop'); setCategory(cat);}}>
                      <span className="cat-emoji">{CATEGORY_EMOJIS[cat] || '🛒'}</span>
                      <span className="cat-name">{cat}</span>
                    </button>
                  ))}
                </div>
              </div>
            </section>
            {activeSpecials.length > 0 && <section className="specials">
              <div className="container">
                <h2 className="specials-title"><span className="flash-title">🔥 Today's Specials</span></h2>
                <div className="specials-grid">
                  {activeSpecials.map(p => (
                    <div key={p.id} className="special-card">
                      {p.discount > 0 && <div className="badge-discount">-{p.discount}%</div>}
                      <img src={imgSrc(p.image)} alt={p.name} />
                      <h3>{p.name}</h3>
                      <div className="price-section">
                        <span className="old-price">₦{p.original_price}</span>
                        <span className="new-price">₦{p.sale_price}</span>
                      </div>
                      <button className="btn-add" onClick={() => addToCart({id: `special-${p.id}`, name: p.name, price: p.sale_price, image: p.image, rating: 4.5, reviews: 0, category: 'Special'})}>Add</button>
                    </div>
                  ))}
                </div>
              </div>
            </section>}
            {(() => {
              const items = [
                ...data.hampers.filter(h => h.active !== false).map(h => ({ key: `h${h.id}`, tag: '🎁 Hamper', name: h.name, sub: `₦${(h.price || 0).toLocaleString()}`, image: h.image, go: 'hampers' })),
                ...data.recipes.filter(r => r.active !== false).map(r => ({ key: `r${r.id}`, tag: '🍽️ Recipe', name: r.name, sub: [r.time, r.difficulty].filter(Boolean).join(' · ') || 'Cook it tonight', image: r.image, go: 'recipes' })),
              ];
              if (items.length === 0) return null;
              const loop = items.length < 6 ? [...items, ...items, ...items] : items;
              return (
                <section className="feature-marquee" aria-label="Hampers and recipes">
                  <div className="marquee-head container">
                    <h2><span className="flash-dot" /> Hampers &amp; Recipes</h2>
                    <div className="marquee-links">
                      <button className="link-view-all" onClick={() => setPage('hampers')}>All hampers →</button>
                      <button className="link-view-all" onClick={() => setPage('recipes')}>All recipes →</button>
                    </div>
                  </div>
                  <div className="marquee-viewport">
                    <div className="marquee-track" style={{ animationDuration: `${Math.max(20, loop.length * 5)}s` }}>
                      {[...loop, ...loop].map((it, i) => (
                        <button key={`${it.key}-${i}`} className="marquee-card" onClick={() => setPage(it.go)} aria-hidden={i >= loop.length}>
                          <img src={imgSrc(it.image)} alt="" loading="lazy" />
                          <span className={`marquee-tag ${it.go}`}>{it.tag}</span>
                          <strong>{it.name}</strong>
                          <span className="marquee-sub">{it.sub}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </section>
              );
            })()}
            <section className="channel-strip">
              <div className="container channel-strip-inner">
                <div>
                  <strong>📢 Never miss a deal</strong>
                  <p>Flash sales, new arrivals and exclusive codes — posted first on our WhatsApp Channel.</p>
                </div>
                <button className="btn-channel" onClick={() => joinChannel('homepage')}>Join on WhatsApp</button>
              </div>
            </section>
            <section className="featured">
              <div className="container">
                <div className="section-header">
                  <div>
                    <h2>⭐ Our Brand Bestsellers</h2>
                    <p className="section-sub">Nkatanni-packaged spices, drinks and staples — made by us, trusted by thousands.</p>
                  </div>
                  <button className="link-view-all" onClick={() => {setPage('shop'); setCategory('Nkatanni Brand');}}>View all →</button>
                </div>
                <div className="grid">
                  {shopProducts.filter(isNkatanniBrand).slice(0, 8).map(p => (
                    <div key={p.id} className="card">
                      <span className="brand-badge">⭐ Our Brand</span>
                      <img src={imgSrc(p.image)} alt={p.name} onClick={() => openProductDetail(p)} style={{cursor: 'pointer'}} />
                      <h3 onClick={() => openProductDetail(p)} style={{cursor: 'pointer'}}>{p.name}</h3>
                      <p className="price">₦{p.price}</p>
                      <button className="btn-add" disabled={isOutOfStock(p)} onClick={() => addToCart(p)}>{isOutOfStock(p) ? 'Out of stock' : 'Add'}</button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            <section className="locations">
              <div className="container">
                <h2>Visit Our Stores</h2>
                <div className="loc-grid">
                  {shopLocations.map((loc, i) => (
                    <div key={i} className="loc-card">
                      <h3>{loc.name}</h3>
                      <p><MapPin size={14} /> {loc.address}</p>
                      <p><Phone size={14} /> {loc.phone}</p>
                      <p><Send size={14} /> <button className="link-btn" onClick={() => window.open(`https://wa.me/${loc.whatsapp.replace(/\D/g, '')}`, '_blank')}>{loc.whatsapp}</button></p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        {page === 'shop' && (
          <div className="container shop">
            <div className="shop-flex">
              <aside className="sidebar">
                <div className="sidebar-header">
                  <h3>Categories</h3>
                  {(category !== 'All Products' || searchQuery) && (
                    <button className="clear-filters" onClick={() => {setCategory('All Products'); setSearchQuery('');}}>Clear</button>
                  )}
                </div>
                {sidebarCategories.map(cat => {
                  const count = cat === 'All Products'
                    ? shopProducts.length
                    : cat === 'Nkatanni Brand'
                      ? shopProducts.filter(isNkatanniBrand).length
                      : shopProducts.filter(p => p.category === cat).length;
                  return (
                    <label key={cat} className={`${category === cat ? 'cat-active' : ''} ${cat === 'Nkatanni Brand' ? 'cat-brand-label' : ''}`}>
                      <input type="radio" checked={category === cat} onChange={() => setCategory(cat)} />
                      <span>{cat === 'Nkatanni Brand' ? '⭐ ' + cat : cat}</span>
                      <span className="cat-count">{count}</span>
                    </label>
                  );
                })}
              </aside>
              <div className="shop-main">
                <div className="mobile-pills">
                  {sidebarCategories.map(cat => (
                    <button key={cat} className={`pill ${category === cat ? 'pill-active' : ''} ${cat === 'Nkatanni Brand' ? 'pill-brand' : ''}`} onClick={() => setCategory(cat)}>
                      {cat === 'Nkatanni Brand' ? '⭐ ' + cat : cat}
                    </button>
                  ))}
                </div>
                <div className="search-bar">
                  <Search size={18} />
                  <input type="text" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                  {searchQuery && <button className="search-clear" onClick={() => setSearchQuery('')}><X size={16}/></button>}
                </div>
                <div className="results-count">
                  {filtered.length} {filtered.length === 1 ? 'product' : 'products'}{category !== 'All Products' ? ` in ${category}` : ''}{searchQuery ? ` matching "${searchQuery}"` : ''}
                </div>
                {filtered.length === 0 ? (
                  <div className="no-results">
                    <p>No products found.</p>
                    <button className="btn-outline" onClick={() => {setCategory('All Products'); setSearchQuery('');}}>Clear filters</button>
                  </div>
                ) : (
                <div className="grid">
                  {filtered.map(p => {
                    const productVariants = getProductVariants(p.id);
                    return (
                      <div key={p.id} className={`card ${isOutOfStock(p) ? 'card-oos' : ''}`}>
                        {isNkatanniBrand(p) && <span className="brand-badge">⭐ Our Brand</span>}
                        {isOutOfStock(p) && <span className="oos-badge">Out of stock</span>}
                        <img src={imgSrc(p.image)} alt={p.name} onClick={() => openProductDetail(p)} style={{cursor: 'pointer'}} />
                        <h3 onClick={() => openProductDetail(p)} style={{cursor: 'pointer'}}>{p.name}</h3>
                        <p className="price">₦{p.price}</p>
                        {isOutOfStock(p) ? (
                          <button className="btn-add" disabled>Out of stock</button>
                        ) : productVariants.length > 0 ? (
                          <div className="variant-selector">
                            <select className="variant-dropdown" onChange={(e) => setSelectedVariant({...selectedVariant, [p.id]: e.target.value})} value={selectedVariant[p.id] || ''}>
                              <option value="">Select variant...</option>
                              {productVariants.map(v => (<option key={v.id} value={v.id}>{v.variant_name} ({v.size}) - ₦{v.price}</option>))}
                            </select>
                            <button className="btn-add" onClick={() => {
                              if (!selectedVariant[p.id]) { alert('Please select a variant'); return; }
                              addToCart(p, parseInt(selectedVariant[p.id]));
                              setSelectedVariant({...selectedVariant, [p.id]: ''});
                            }}>Add</button>
                          </div>
                        ) : (<button className="btn-add" onClick={() => addToCart(p)}>Add</button>)}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {page === 'recipes' && (
          <div className="container page">
            <h1>Authentic African Recipes</h1>
            <p className="subtitle">Discover traditional meals with our premium ingredients</p>
            <div className="recipes-grid">
              {data.recipes.filter(r => r.active !== false).map(r => (
                <div key={r.id} className="recipe-card">
                  <img src={imgSrc(r.image)} alt={r.name} />
                  <h3>{r.name}</h3>
                  <div className="recipe-meta">{r.time} | {r.difficulty}</div>
                  {r.description && <p className="recipe-desc">{r.description}</p>}
                  <h4>Ingredients:</h4>
                  <ul>{r.ingredients && r.ingredients.map((ing, i) => <li key={i}>✓ {ing}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {page === 'hampers' && (
          <div className="container page">
            <h1>Food Hampers & Bundles</h1>
            <p className="subtitle">Curated packages for families and special occasions</p>
            <div className="hampers-grid">
              {data.hampers.filter(h => h.active !== false).map(h => (
                <div key={h.id} className="hamper-card">
                  <button className="hamper-poster" onClick={() => setZoomImg({ src: imgSrc(h.image), alt: h.name })} title="Tap to view full size">
                    <img src={imgSrc(h.image)} alt={h.name} loading="lazy" />
                    <span className="hamper-zoom">🔍 Tap to view</span>
                  </button>
                  <h3>{h.name}</h3>
                  <p>{h.desc}</p>
                  <div className="hamper-contents">
                    <h4>Contents:</h4>
                    <ul>{h.items && h.items.map((item, i) => <li key={i}>✓ {item}</li>)}</ul>
                  </div>
                  <p className="hamper-price">₦{Number(h.price || 0).toLocaleString()}</p>
                  <button className="btn-primary" onClick={() => addToCart({...h, id: `hamper-${h.id}`})}>Add</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {page === 'export' && (
          <div className="container page">
            <h1>Export & Diaspora Services</h1>
            <p className="subtitle">Serving Africans at home and abroad</p>
            <section className="content-section">
              <h2>🌍 Worldwide Shipping</h2>
              <p>We deliver authentic Nigerian and African foodstuff to Africans living abroad, international stores, and food businesses globally. We ship to over 50 countries with express delivery options.</p>
              <div className="service-grid">
                <div className="service-card"><h3>🚚 Fast Delivery</h3><p>Express shipping to over 50 countries worldwide. Secure packaging to preserve product quality.</p></div>
                <div className="service-card"><h3>📦 Bulk Orders</h3><p>Special pricing for international stores and African food businesses. Wholesale discounts available.</p></div>
                <div className="service-card"><h3>💳 Flexible Payment</h3><p>Accept WhatsApp payments, international transfers, and multiple payment methods for convenience.</p></div>
                <div className="service-card"><h3>🏪 Store Supply</h3><p>Dedicated supply service for African stores abroad. Reliable partner for continuous stock.</p></div>
              </div>
            </section>
            <section className="content-section">
              <h2>📍 Nationwide Delivery (Nigeria)</h2>
              <p>Quick and reliable delivery across Nigeria with tracking</p>
              <ul className="service-list">
                <li>✓ Same-day delivery in Owerri</li>
                <li>✓ Next-day delivery to major Nigerian cities</li>
                <li>✓ 2-3 days to remote areas</li>
                <li>✓ Free delivery on orders over ₦100,000</li>
                <li>✓ Tracked shipping with WhatsApp updates</li>
              </ul>
            </section>
          </div>
        )}

        {page === 'about' && (
          <div className="container page">
            <h1>About Nkatanni Food Mart</h1>
            <section className="content-section"><h2>Our Story</h2><p>Nkatanni Food Mart is your trusted online store for authentic African foodstuff and groceries. We believe that quality and accessibility should go hand in hand, making it easy for families and businesses to access premium ingredients online.</p></section>
            <section className="content-section"><h2>Our Mission</h2><p>To make authentic African foodstuff easily accessible to Africans at home and in the diaspora, bringing convenience and reliability to traditional food shopping.</p></section>
            <section className="content-section">
              <h2>Why Choose Nkatanni?</h2>
              <div className="benefits-grid">
                <div className="benefit-card"><h3>🌿 Premium Quality</h3><p>Fresh, authentic ingredients sourced from trusted suppliers in Nigeria and Africa.</p></div>
                <div className="benefit-card"><h3>🛡️ Hygienic Packaging</h3><p>Safe, sealed packaging to preserve freshness and quality throughout delivery.</p></div>
                <div className="benefit-card"><h3>🚚 Fast Delivery</h3><p>Quick shipping nationwide and worldwide. Free delivery on orders over ₦100,000.</p></div>
                <div className="benefit-card"><h3>💬 Easy Checkout</h3><p>Simple WhatsApp ordering without hassle. Browse, add to cart, and checkout in seconds.</p></div>
              </div>
            </section>
          </div>
        )}

        {page === 'faq' && (
          <div className="container page faq-page">
            <h1>Frequently Asked Questions</h1>
            <p className="faq-lead">Everything you need to know about ordering, payment, delivery and our products. Can't find your answer? <a href="https://wa.me/2347055921564" target="_blank" rel="noreferrer">Chat with us on WhatsApp</a>.</p>
            {FAQ_SECTIONS.map(sec => (
              <section className="faq-section" key={sec}>
                <h2>{sec}</h2>
                {FAQS.filter(f => f.s === sec).map(f => (
                  <details className="faq-item" key={f.q}>
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </section>
            ))}
            <div className="faq-cta">
              <button className="btn-primary" onClick={() => setPage('shop')}>Start shopping</button>
              <button className="btn-secondary" onClick={() => window.open('https://wa.me/2347055921564', '_blank')}>Ask on WhatsApp</button>
            </div>
          </div>
        )}

        {page === 'contact' && (
          <div className="container page">
            <h1>Contact Nkatanni Food Mart</h1>
            <div className="channel-strip channel-strip-inline">
              <div className="channel-strip-inner">
                <div><strong>📢 Follow Nkàtànnî (Food Basket) on WhatsApp</strong><p>Get promos and campaign updates before anyone else.</p></div>
                <button className="btn-channel" onClick={() => joinChannel('contact')}>Join Channel</button>
              </div>
            </div>
            <div className="review-strip">
              <div><strong>⭐ Shopped with us?</strong><p>A quick Google review helps more families find Nkatanni.</p></div>
              <button className="btn-review" onClick={() => reviewUs('contact')}>Review us on Google</button>
            </div>
            <div className="contact-grid">
              <section className="contact-section">
                <h2>📍 Our Locations</h2>
                {shopLocations.map((loc, i) => (
                  <div key={i} className="location-detail">
                    <h3>{loc.name}</h3>
                    <p><MapPin size={14} /> {loc.address}</p>
                    <p><Phone size={14} /> <button className="link-btn" onClick={() => window.open(`tel:${loc.phone}`)}>{loc.phone}</button></p>
                    <p><Send size={14} /> <button className="link-btn" onClick={() => window.open(`https://wa.me/${loc.whatsapp.replace(/\D/g, '')}`, '_blank')}>{loc.whatsapp}</button></p>
                    <p>🕐 {loc.hours}</p>
                  </div>
                ))}
              </section>
              <section className="contact-section">
                <h2>🤝 Follow Us On Social Media</h2>
                <div className="socials">
                  <button onClick={() => window.open('https://facebook.com/nkatanni', '_blank')}>📘 Facebook</button>
                  <button onClick={() => window.open('https://instagram.com/nkatanni', '_blank')}>📷 Instagram</button>
                  <button onClick={() => window.open('https://tiktok.com/@nkatanni', '_blank')}>🎵 TikTok</button>
                </div>
              </section>
            </div>
            <section className="content-section">
              <h2>📧 Send us a Message</h2>
              <form className="contact-form" onSubmit={handleContactSubmit}>
                <input type="text" placeholder="Your Name" value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} required />
                <input type="email" placeholder="Your Email" value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} required />
                <input type="tel" placeholder="Your Phone (Optional)" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} />
                <textarea placeholder="Your Message" rows="5" value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} required></textarea>
                <button type="submit" className="btn-primary">Send Message via WhatsApp</button>
                {contactMessage && <p className="contact-msg">{contactMessage}</p>}
              </form>
            </section>
          </div>
        )}

        {page === 'admin' && (
          <div className="container page">
            <h1>⚙️ Admin Dashboard</h1>
            {!adminLoggedIn ? (
              <div className="admin-login">
                <h2>Admin Login</h2>
                <input type="email" placeholder="Admin email" autoComplete="username" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
                <input type="password" placeholder="Password" autoComplete="current-password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAdminLogin(); }} />
                <button onClick={handleAdminLogin} className="btn-primary" disabled={adminBusy}>{adminBusy ? 'Signing in…' : 'Login'}</button>
              </div>
            ) : (
              <div className="admin-panel">
                <div className="admin-header">
                  <div className="admin-nav">
                    <button className={adminTab === 'orders' ? 'active' : ''} onClick={() => { setAdminTab('orders'); loadOrders(); }}>🧾 Orders</button>
                    <button className={adminTab === 'products' ? 'active' : ''} onClick={() => setAdminTab('products')}>📦 Products</button>
                    <button className={adminTab === 'variants' ? 'active' : ''} onClick={() => setAdminTab('variants')}>📊 Variants</button>
                    <button className={adminTab === 'specials' ? 'active' : ''} onClick={() => setAdminTab('specials')}>🔥 Specials</button>
                    <button className={adminTab === 'categories' ? 'active' : ''} onClick={() => setAdminTab('categories')}>📂 Categories</button>
                    <button className={adminTab === 'recipes' ? 'active' : ''} onClick={() => setAdminTab('recipes')}>🍽️ Recipes</button>
                    <button className={adminTab === 'hampers' ? 'active' : ''} onClick={() => setAdminTab('hampers')}>🎁 Hampers</button>
                    <button className={adminTab === 'referrals' ? 'active' : ''} onClick={() => setAdminTab('referrals')}>🎯 Codes</button>
                    <button className={adminTab === 'campaigns' ? 'active' : ''} onClick={() => setAdminTab('campaigns')}>📢 Campaigns</button>
                    <button onClick={handleAdminLogout} className="btn-logout">Logout</button>
                  </div>
                </div>
                {adminTab === 'products' && (
                  <div className="admin-content">
                    <h2>📦 Product Management</h2>
                    <div className="form-section">
                      <h3>Add Product</h3>
                      <div className="form-grid">
                        <input type="text" placeholder="Name" value={formData.product.name} onChange={(e) => setFormData({...formData, product: {...formData.product, name: e.target.value}})} />
                        <select value={formData.product.category} onChange={(e) => setFormData({...formData, product: {...formData.product, category: e.target.value}})}>
                          <option value="">Select Category</option>
                          {data.categories.filter(c => c !== 'All Products').map(cat => <option key={cat}>{cat}</option>)}
                        </select>
                        <input type="number" placeholder="Base Price" value={formData.product.price} onChange={(e) => setFormData({...formData, product: {...formData.product, price: e.target.value}})} />
                        <button onClick={addProduct} className="btn-primary">Add Product</button>
                      </div>
                    </div>
                    <div className="admin-filter">
                      <input type="text" placeholder="Search products..." value={adminSearch} onChange={(e) => setAdminSearch(e.target.value)} />
                      <label><input type="checkbox" checked={adminNoPhoto} onChange={(e) => setAdminNoPhoto(e.target.checked)} /> Missing photo only</label>
                      <label><input type="checkbox" checked={adminWaOnly} onChange={(e) => setAdminWaOnly(e.target.checked)} /> On WhatsApp only ({data.products.filter(p => p.on_whatsapp).length})</label>
                      <label><input type="checkbox" checked={adminHiddenOnly} onChange={(e) => setAdminHiddenOnly(e.target.checked)} /> Hidden only ({data.products.filter(p => p.hidden).length})</label>
                    </div>
                    {(() => {
                      const q = adminSearch.trim().toLowerCase();
                      const rows = data.products.filter(p => (!q || (p.name || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q)) && (!adminNoPhoto || !p.image) && (!adminWaOnly || p.on_whatsapp) && (!adminHiddenOnly || p.hidden));
                      return (
                        <>
                          <p className="admin-count">{rows.length} products</p>
                          <div className="admin-table-wrap">
                            <table className="admin-table admin-products">
                              <thead><tr><th>Photo</th><th>Name &amp; description</th><th>Category</th><th>Price (₦)</th><th>In stock</th><th title="Show in the WhatsApp catalogue (updates daily)">WhatsApp</th><th title="Show this product on the website">On site</th><th></th></tr></thead>
                              <tbody>
                                {rows.map(p => {
                                  const e = productEdits[p.id] || {};
                                  const val = (f) => (f in e ? e[f] : p[f] ?? '');
                                  const dirty = Object.keys(e).length > 0;
                                  return (
                                    <tr key={p.id} className={`${isOutOfStock(p) ? 'row-oos' : ''} ${p.hidden ? 'row-hidden' : ''}`}>
                                      <td>
                                        <label className="photo-cell" title="Change photo">
                                          <img src={imgSrc(p.image)} alt="" />
                                          <span>{uploadingId === p.id ? 'Uploading…' : (p.image ? 'Change' : 'Add photo')}</span>
                                          <input type="file" accept="image/*" hidden disabled={uploadingId === p.id} onChange={(ev) => { handlePhotoUpload(p, ev.target.files[0]); ev.target.value = ''; }} />
                                        </label>
                                      </td>
                                      <td>
                                        <input className="cell-input" value={val('name')} onChange={(ev) => editProduct(p.id, 'name', ev.target.value)} />
                                        <textarea className="cell-input cell-desc" rows={2} placeholder="Short description (shown on the product page and WhatsApp)" value={val('description')} onChange={(ev) => editProduct(p.id, 'description', ev.target.value)} />
                                        <a className="cell-link" href={productPath(p)} target="_blank" rel="noreferrer">View page ↗</a>
                                      </td>
                                      <td>
                                        <select className="cell-input" value={val('category')} onChange={(ev) => editProduct(p.id, 'category', ev.target.value)}>
                                          {!data.categories.includes(p.category) && <option value={p.category}>{p.category}</option>}
                                          {data.categories.filter(c => c !== 'All Products').map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                        </select>
                                      </td>
                                      <td><input className="cell-input cell-price" type="number" min="0" value={val('price')} onChange={(ev) => editProduct(p.id, 'price', ev.target.value)} onKeyDown={(ev) => { if (ev.key === 'Enter' && dirty) saveProduct(p); }} /></td>
                                      <td><label className="stock-toggle"><input type="checkbox" checked={!isOutOfStock(p)} onChange={() => toggleStock(p)} /> {isOutOfStock(p) ? 'No' : 'Yes'}</label></td>
                                      <td><label className="stock-toggle wa-toggle"><input type="checkbox" checked={!!p.on_whatsapp} onChange={() => toggleWhatsapp(p)} /> {p.on_whatsapp ? 'Yes' : 'No'}</label></td>
                                      <td><label className="stock-toggle"><input type="checkbox" checked={!p.hidden} onChange={() => toggleHidden(p)} /> {p.hidden ? 'Hidden' : 'Yes'}</label></td>
                                      <td className="row-actions">
                                        <button className="btn-row-save" disabled={!dirty} onClick={() => saveProduct(p)}>Save</button>
                                        <button className="btn-delete" onClick={() => handleDelete('products', p.id, p.name)}><Trash2 size={14} /></button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
                {adminTab === 'orders' && (() => {
                  const parseItems = (it) => { if (Array.isArray(it)) return it; try { const v = JSON.parse(it); return Array.isArray(v) ? v : []; } catch (e) { return []; } };
                  const fmtDate = (d) => { if (!d) return ''; const iso = /Z|[+-]\d\d:?\d\d$/.test(d) ? d : d + 'Z'; return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); };
                  const ACTIVE = ['pending', 'paid'];
                  const count = (st) => orders.filter(o => (o.status || 'pending') === st).length;
                  const sales = orders.filter(o => o.paid).reduce((a, o) => a + (o.total || 0), 0);
                  const owed = orders.filter(o => !o.paid && (o.status || 'pending') !== 'cancelled');
                  const owedTotal = owed.reduce((a, o) => a + (o.total || 0), 0);
                  const rows = orders.filter(o => {
                    const st = o.status || 'pending';
                    if (orderFilter === 'unpaid') return !o.paid && st !== 'cancelled';
                    return orderFilter === 'all' ? true : orderFilter === 'active' ? ACTIVE.includes(st) : st === orderFilter;
                  });
                  const markPaid = (o, paid) => updateOrder(o, paid
                    ? { paid: true, paid_at: new Date().toISOString(), ...(o.status === 'pending' || !o.status ? { status: 'paid' } : {}) }
                    : { paid: false, paid_at: null, ...(o.status === 'paid' ? { status: 'pending' } : {}) });
                  const remind = async (o) => {
                    if (!o.customer_phone) { alert('Add the customer phone number first'); return; }
                    window.open(`https://wa.me/${waNumber(o.customer_phone)}?text=${encodeURIComponent(reminderText(o))}`, '_blank');
                    await updateOrder(o, { reminded_at: new Date().toISOString(), reminder_count: (o.reminder_count || 0) + 1 });
                  };
                  const daysAgo = (d) => { if (!d) return ''; const iso = /Z|[+-]\d\d:?\d\d$/.test(d) ? d : d + 'Z'; const n = Math.floor((Date.now() - new Date(iso)) / 86400000); return n <= 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`; };
                  const invoiceFor = async (o) => { try { await downloadInvoice(orderToInvoice(o)); } catch (e) { alert(e.message); } };
                  const ni = newInv;
                  const niLines = ni ? ni.lines.split('\n').map(l => l.trim()).filter(Boolean).map(l => { const p = l.split('|').map(x => x.trim()); const amt = parseInt(String(p[p.length - 1] || '').replace(/[^0-9]/g, ''), 10) || 0; return { name: p[0], qty_label: p.length > 2 ? p[1] : '1', qty: 1, price: amt, amount: amt }; }) : [];
                  const niSub = niLines.reduce((a, l) => a + l.amount, 0);
                  const niFee = ni ? (parseInt(ni.fee, 10) || 0) : 0;
                  const createInvoice = async () => {
                    if (!ni.name.trim()) { alert('Enter the customer name'); return; }
                    if (!niLines.length || niLines.some(l => !l.name || !l.amount)) { alert('Each line needs: description | quantity | amount'); return; }
                    const row = { ref: makeRef(), customer_name: ni.name.trim(), customer_phone: ni.phone.trim() || null, address: ni.address.trim() || null, delivery_area: null, delivery_fee: niFee, items: JSON.stringify(niLines), subtotal: niSub, total: niSub + niFee, status: 'pending', paid: false, source: 'manual' };
                    const { error } = await supabase.from('orders').insert(row);
                    if (error) { alert(`Could not save invoice: ${error.message}`); return; }
                    try { await downloadInvoice(orderToInvoice({ ...row, created_at: new Date().toISOString() })); } catch (e) { alert(e.message); }
                    setNewInv(null); loadOrders();
                  };
                  return (
                    <div className="admin-content">
                      <div className="orders-head">
                        <h2>🧾 Orders</h2>
                        <div className="orders-head-btns">
                          <button className="btn-primary" onClick={() => setNewInv(ni ? null : { name: '', phone: '', address: '', fee: '2500', lines: '' })}>{ni ? 'Close' : '＋ New invoice'}</button>
                          <button className="btn-outline" onClick={loadOrders} disabled={ordersLoading}>{ordersLoading ? 'Loading…' : '↻ Refresh'}</button>
                        </div>
                      </div>
                      {ni && (
                        <div className="form-section new-invoice">
                          <h3>New invoice (orders taken by phone, WhatsApp or in store)</h3>
                          <div className="form-grid">
                            <input placeholder="Customer name *" value={ni.name} onChange={(e) => setNewInv({ ...ni, name: e.target.value })} />
                            <input placeholder="Phone" value={ni.phone} onChange={(e) => setNewInv({ ...ni, phone: e.target.value })} />
                            <input placeholder="Address" value={ni.address} onChange={(e) => setNewInv({ ...ni, address: e.target.value })} />
                            <input type="number" min="0" placeholder="Delivery fee (₦)" value={ni.fee} onChange={(e) => setNewInv({ ...ni, fee: e.target.value })} />
                          </div>
                          <textarea className="ni-lines" rows={8} placeholder={'One item per line:  description | quantity | amount\nGarri mix | 1 carton | 18000\nMilo (big packets) | 3 packets | 26700'} value={ni.lines} onChange={(e) => setNewInv({ ...ni, lines: e.target.value })} />
                          <div className="ni-total">{niLines.length} item{niLines.length !== 1 ? 's' : ''} · Subtotal ₦{niSub.toLocaleString()} · Delivery ₦{niFee.toLocaleString()} · <strong>Total ₦{(niSub + niFee).toLocaleString()}</strong></div>
                          <button className="btn-primary" onClick={createInvoice}>Save &amp; download invoice</button>
                        </div>
                      )}
                      <div className="order-stats">
                        <div><strong>{count('pending')}</strong><span>New / pending</span></div>
                        <div><strong>{count('paid')}</strong><span>Paid, to deliver</span></div>
                        <div><strong>{count('delivered')}</strong><span>Delivered</span></div>
                        <div><strong>₦{sales.toLocaleString()}</strong><span>Paid value</span></div>
                        <div className="stat-owed"><strong>₦{owedTotal.toLocaleString()}</strong><span>Unpaid ({owed.length})</span></div>
                      </div>
                      <div className="order-filters">
                        {[['active', 'To do'], ['unpaid', 'Unpaid'], ['pending', 'Pending'], ['paid', 'Paid'], ['delivered', 'Delivered'], ['cancelled', 'Cancelled'], ['all', 'All']].map(([k, label]) => (
                          <button key={k} className={`pill ${orderFilter === k ? 'pill-active' : ''}`} onClick={() => setOrderFilter(k)}>{label}</button>
                        ))}
                      </div>
                      {rows.length === 0 ? <p className="admin-count">{ordersLoading ? 'Loading orders…' : 'No orders here.'}</p> : (
                        <div className="order-list">
                          {rows.map(o => {
                            const st = o.status || 'pending';
                            const items = parseItems(o.items);
                            const open = openOrderId === o.id;
                            const ed = orderEdits[o.id] || {};
                            const v = (f) => (f in ed ? ed[f] : o[f] || '');
                            return (
                              <div key={o.id} className={`order-card st-${st}`}>
                                <div className="order-row" onClick={() => setOpenOrderId(open ? null : o.id)}>
                                  <div className="order-main">
                                    <span className="order-ref">{o.ref || `#${o.id}`}</span>
                                    <span className="order-date">{fmtDate(o.created_at)}</span>
                                    <span className="order-cust">{o.customer_name || 'Customer'}{o.customer_phone ? ` · ${o.customer_phone}` : ''}</span>
                                  </div>
                                  <div className="order-side">
                                    <span className="order-total">₦{(o.total || 0).toLocaleString()}</span>
                                    <span className={`order-status s-${st}`}>{st}</span>
                                    <span className={`pay-badge ${o.paid ? 'is-paid' : 'is-unpaid'}`}>{o.paid ? 'Paid' : 'Unpaid'}</span>
                                    {!o.paid && o.reminded_at && <span className="remind-note" title={`${o.reminder_count || 1} reminder(s) sent`}>🔔 {daysAgo(o.reminded_at)}</span>}
                                  </div>
                                </div>
                                <div className="order-line">{items.length} item{items.length !== 1 ? 's' : ''}: {items.slice(0, 3).map(i => i.name).join(', ')}{items.length > 3 ? '…' : ''}</div>
                                {open && (
                                  <div className="order-detail">
                                    <table className="order-items">
                                      <tbody>
                                        {items.map((i, idx) => (
                                          <tr key={idx}><td>{i.name}</td><td>{i.qty_label || `×${i.qty || 1}`}</td><td>₦{(i.amount != null ? i.amount : (i.price || 0) * (i.qty || 1)).toLocaleString()}</td></tr>
                                        ))}
                                        <tr className="order-sub"><td>Subtotal</td><td></td><td>₦{(o.subtotal || 0).toLocaleString()}</td></tr>
                                        {o.promo_code && <tr><td>Promo: {o.promo_code}</td><td></td><td></td></tr>}
                                        <tr className="order-sub"><td><strong>Total</strong></td><td></td><td><strong>₦{(o.total || 0).toLocaleString()}</strong></td></tr>
                                      </tbody>
                                    </table>
                                    {(o.delivery_area || o.address) && <p className="order-addr">🚚 {o.delivery_area}{o.address ? ` — ${o.address}` : ''}{o.delivery_fee != null ? ` (delivery ${o.delivery_fee === 0 ? 'free' : '₦' + o.delivery_fee.toLocaleString()})` : ''}</p>}
                                    <div className="order-customer">
                                      <input placeholder="Customer name" value={v('customer_name')} onChange={(e) => setOrderEdits(prev => ({ ...prev, [o.id]: { ...prev[o.id], customer_name: e.target.value } }))} />
                                      <input placeholder="Phone" value={v('customer_phone')} onChange={(e) => setOrderEdits(prev => ({ ...prev, [o.id]: { ...prev[o.id], customer_phone: e.target.value } }))} />
                                      <button className="btn-row-save" disabled={!orderEdits[o.id]} onClick={() => saveOrderCustomer(o)}>Save</button>
                                    </div>
                                    <div className="order-actions">
                                      <button className="btn-primary" onClick={() => invoiceFor(o)}>🧾 Invoice</button>
                                      {!o.paid && st !== 'cancelled' && <button className="btn-remind" onClick={() => remind(o)} title="Send a payment reminder on WhatsApp">🔔 Remind{o.reminder_count ? ` (${o.reminder_count})` : ''}</button>}
                                      {o.paid ? <button className="btn-outline" onClick={() => markPaid(o, false)}>Mark unpaid</button> : <button className="btn-primary" onClick={() => markPaid(o, true)}>Mark paid</button>}
                                      {(st === 'pending' || st === 'paid') && <button className="btn-primary" onClick={() => updateOrder(o, { status: 'delivered' })}>Mark delivered</button>}
                                      {st !== 'cancelled' && st !== 'delivered' && <button className="btn-outline" onClick={() => updateOrder(o, { status: 'cancelled' })}>Cancel order</button>}
                                      {st !== 'pending' && <button className="btn-outline" onClick={() => updateOrder(o, { status: 'pending' })}>Back to pending</button>}
                                      {o.customer_phone && <button className="btn-outline" onClick={() => window.open(`https://wa.me/${waNumber(o.customer_phone)}`, '_blank')}>WhatsApp customer</button>}
                                      <button className="btn-delete" onClick={() => deleteOrder(o)}><Trash2 size={14} /></button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {adminTab === 'specials' && (
                  <AdminItemEditor
                    title="🔥 Today's Specials"
                    header={(() => {
                      const q = spSearch.trim().toLowerCase();
                      const choices = data.products.filter(p => !q || p.name.toLowerCase().includes(q)).slice(0, 100);
                      const prod = data.products.find(p => String(p.id) === String(spProductId));
                      const sale = parseInt(spPrice, 10);
                      const addFromProduct = async () => {
                        if (!prod) { alert('Choose a product'); return; }
                        if (Number.isNaN(sale) || sale <= 0) { alert('Enter the special price'); return; }
                        if (sale >= prod.price && !window.confirm('Special price is not lower than the normal price. Add anyway?')) return;
                        const ok = await specialHandlers.onAdd({ name: prod.name, original_price: prod.price, sale_price: sale, image: prod.image || null });
                        if (ok) { setSpProductId(''); setSpPrice(''); setSpSearch(''); }
                      };
                      return (
                        <div className="form-section special-picker">
                          <h3>Pick from your products</h3>
                          <div className="admin-filter">
                            <input type="text" placeholder="Search products..." value={spSearch} onChange={(e) => setSpSearch(e.target.value)} />
                            <select value={spProductId} onChange={(e) => setSpProductId(e.target.value)}>
                              <option value="">— Select product ({choices.length}) —</option>
                              {choices.map(p => <option key={p.id} value={p.id}>{p.name} — ₦{p.price}</option>)}
                            </select>
                          </div>
                          {prod && (
                            <div className="special-preview">
                              <img src={imgSrc(prod.image)} alt="" />
                              <div>
                                <strong>{prod.name}</strong>
                                <p>Normal price: ₦{prod.price}</p>
                                <input type="number" min="0" placeholder="Special price (₦)" value={spPrice} onChange={(e) => setSpPrice(e.target.value)} />
                                {!Number.isNaN(sale) && sale > 0 && sale < prod.price && <p className="admin-hint">{Math.round((1 - sale / prod.price) * 100)}% off</p>}
                                <button className="btn-primary" onClick={addFromProduct}>Add as Special</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    addLabel="Add Special"
                    items={data.dailySpecials}
                    fields={[
                      { key: 'name', label: 'Name', required: true },
                      { key: 'original_price', label: 'Normal price (₦)', type: 'number', required: true },
                      { key: 'sale_price', label: 'Special price (₦)', type: 'number', required: true },
                    ]}
                    uploadingKey={uploadingId}
                    uploadPrefix="special"
                    {...specialHandlers}
                  />
                )}
                {adminTab === 'hampers' && (
                  <AdminItemEditor
                    title="🎁 Hampers & Bundles"
                    addLabel="Add Hamper"
                    items={data.hampers}
                    fields={[
                      { key: 'name', label: 'Name', required: true },
                      { key: 'price', label: 'Price (₦)', type: 'number', required: true },
                      { key: 'desc', label: 'Description', type: 'textarea' },
                      { key: 'items', label: 'Contents', type: 'list', required: true },
                    ]}
                    uploadingKey={uploadingId}
                    uploadPrefix="hamper"
                    {...hamperHandlers}
                  />
                )}
                {adminTab === 'recipes' && (
                  <AdminItemEditor
                    title="🍽️ Recipes"
                    addLabel="Add Recipe"
                    items={data.recipes}
                    fields={[
                      { key: 'name', label: 'Name', required: true },
                      { key: 'time', label: 'Cooking time (e.g. 45 mins)' },
                      { key: 'difficulty', label: 'Difficulty (Easy / Medium / Hard)' },
                      { key: 'description', label: 'Short description', type: 'textarea' },
                      { key: 'ingredients', label: 'Ingredients', type: 'list', required: true },
                    ]}
                    uploadingKey={uploadingId}
                    uploadPrefix="recipe"
                    {...recipeHandlers}
                  />
                )}
                {adminTab === 'referrals' && (
                  <AdminItemEditor
                    title="🎯 Promo Codes"
                    addLabel="Add Code"
                    hint="Showing codes are advertised in the orange bar at the top of the site (first two) and customers can tap them to apply. Untick to hide a code and switch it off."
                    items={data.referralCodes}
                    fields={[
                      { key: 'code', label: 'Code (e.g. WELCOME10)', required: true },
                      { key: 'discount', label: 'Discount %', type: 'number', required: true },
                    ]}
                    noPhoto
                    {...codeHandlers}
                  />
                )}
                {adminTab === 'campaigns' && (
                  <AdminItemEditor
                    title="📢 Campaigns"
                    addLabel="Add Campaign"
                    hint="A showing campaign appears in the orange bar at the top of the site and its % off is applied to every cart automatically. If several are on, the biggest one wins. It does not stack with promo codes; the customer gets whichever is bigger."
                    items={data.campaigns}
                    fields={[
                      { key: 'name', label: 'Campaign name', required: true },
                      { key: 'discount', label: 'Discount %', type: 'number', required: true },
                    ]}
                    noPhoto
                    {...campaignHandlers}
                  />
                )}
                {adminTab === 'variants' && (() => {
                  const vq = variantSearch.trim().toLowerCase();
                  const pid = parseInt(variantProductId, 10);
                  const choices = data.products.filter(p => !vq || p.name.toLowerCase().includes(vq)).slice(0, 100);
                  const product = data.products.find(p => p.id === pid);
                  const picker = (
                    <div className="form-section variant-picker">
                      <h3>Choose a product</h3>
                      <div className="admin-filter">
                        <input type="text" placeholder="Search products..." value={variantSearch} onChange={(e) => setVariantSearch(e.target.value)} />
                        <select value={variantProductId} onChange={(e) => setVariantProductId(e.target.value)}>
                          <option value="">— Select product ({choices.length}) —</option>
                          {choices.map(p => <option key={p.id} value={p.id}>{p.name} ({data.variants.filter(v => v.product_id === p.id).length})</option>)}
                        </select>
                      </div>
                    </div>
                  );
                  return product ? (
                    <AdminItemEditor
                      key={product.id}
                      title="📊 Sizes & Variants"
                      header={<>{picker}<p className="admin-count">Sizes for <strong>{product.name}</strong> (base price ₦{product.price}). Customers pick one of these on the product card.</p></>}
                      addLabel="Add Size"
                      items={data.variants.filter(v => v.product_id === product.id)}
                      fields={[
                        { key: 'variant_name', label: 'Name (e.g. Small)', required: true },
                        { key: 'size', label: 'Size (e.g. 500g)', required: true },
                        { key: 'price', label: 'Price (₦)', type: 'number', required: true },
                      ]}
                      noPhoto
                      {...variantHandlers}
                    />
                  ) : (
                    <div className="admin-content"><h2>📊 Sizes & Variants</h2>{picker}</div>
                  );
                })()}
                {adminTab === 'categories' && (
                  <div className="admin-content">
                    <h2>📂 Categories</h2>
                    <div className="form-section">
                      <h3>Add Category</h3>
                      <div className="form-grid">
                        <input type="text" placeholder="Category name" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} />
                        <button className="btn-primary" onClick={addCategory}>Add Category</button>
                      </div>
                    </div>
                    <p className="admin-hint admin-hint-top">Renaming a category moves all its products with it. A category can only be deleted once it is empty.</p>
                    <div className="item-list">
                      {data.categoryRows.slice().sort((a, b) => a.name.localeCompare(b.name)).map(row => {
                        const n = productCountIn(row.name);
                        const val = row.id in categoryEdits ? categoryEdits[row.id] : row.name;
                        return (
                          <div key={row.id} className="item-card cat-row">
                            <input className="cell-input cat-input" value={val} onChange={(e) => setCategoryEdits(prev => ({ ...prev, [row.id]: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') renameCategory(row); }} />
                            <span className="cat-count">{n} product{n !== 1 ? 's' : ''}</span>
                            <button className="btn-row-save" disabled={!(row.id in categoryEdits) || !val.trim() || val.trim() === row.name} onClick={() => renameCategory(row)}>Rename</button>
                            <button className="btn-delete" onClick={() => deleteCategory(row)}><Trash2 size={14} /></button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Other admin tabs unchanged - keeping all your existing admin functionality */}
              </div>
            )}
          </div>
        )}

        {page === 'cart' && (
          <div className="container cart-page">
            <h1>🛒 Shopping Cart</h1>
            {cart.length === 0 ? (
              <div className="empty-cart">
                <div className="empty-icon">🛒</div>
                <p className="empty-text">Your cart is empty</p>
                <p className="empty-subtext">Add items to get started</p>
                <button className="btn-primary" onClick={() => setPage('shop')}>Start Shopping</button>
              </div>
            ) : (
              <div className="checkout-container">
                <div className="cart-main">
                  <div className="cart-header">
                    <h2>Items ({cart.length})</h2>
                    <button className="clear-btn" onClick={() => { if (window.confirm('Clear all items from cart?')) { setCart([]); } }}>🗑️ Clear Cart</button>
                  </div>
                  <div className="cart-items-list">
                    {cart.map(item => (
                      <div key={item.id} className="cart-item-card">
                        <div className="item-image"><img src={imgSrc(item.image)} alt={item.name} /></div>
                        <div className="item-details">
                          <h3>{item.name}</h3>
                          <p className="item-price">₦{item.price.toLocaleString()} each</p>
                          <p className="item-total">Total: <strong>₦{(item.price * item.qty).toLocaleString()}</strong></p>
                        </div>
                        <div className="qty-and-delete">
                          <div className="qty-controls">
                            <button className="qty-btn" aria-label="Decrease quantity" onClick={() => updateQty(item.id, item.qty - 1)}>−</button>
                            <input type="number" className="qty-input" value={item.qty} onChange={(e) => updateQty(item.id, parseInt(e.target.value) || 1)} min="1" />
                            <button className="qty-btn" aria-label="Increase quantity" onClick={() => updateQty(item.id, item.qty + 1)}>+</button>
                          </div>
                          <button className="remove-btn" onClick={() => removeFromCart(item.id)}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="checkout-sidebar">
                  <div className="promo-section">
                    <h3>Apply Promo Code</h3>
                    <div className="promo-input-group">
                      <input type="text" placeholder="Enter code" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} className="promo-input" />
                      <button className="btn-apply" onClick={() => {
                        if (!promoCode) { alert('Enter a promo code'); return; }
                        const validCode = data.referralCodes.find(c => c.code === promoCode && c.active);
                        if (validCode) { alert(`✓ ${validCode.discount}% discount applied!`); }
                        else { alert('Invalid or inactive promo code'); setPromoCode(''); }
                      }}>Apply</button>
                    </div>
                    {activeCampaign && !activePromoCode && <p className="campaign-note">📢 {activeCampaign.name}: {activeCampaign.discount}% off applied automatically</p>}
                    {activePromoCode && codeDiscount < campaignDiscount && <p className="campaign-note">Your code gives {codeDiscount}%, so the better {activeCampaign.name} offer ({campaignDiscount}%) is applied instead.</p>}
                    {activePromoCode && (
                      <div className="promo-success">
                        <p>✓ <strong>{activePromoCode.discount}% off</strong> applied</p>
                        <button className="clear-promo" onClick={() => setPromoCode('')}>Remove code</button>
                      </div>
                    )}
                  </div>
                  <div className={`free-delivery-card ${total >= FREE_DELIVERY_MIN ? 'won' : ''}`}>
                    {total >= FREE_DELIVERY_MIN ? (
                      <p>🎉 <strong>FREE delivery unlocked!</strong> You're saving {naira(DELIVERY_FEE)}.</p>
                    ) : (
                      <>
                        <p>🚚 You're <strong>{naira(FREE_DELIVERY_MIN - total)}</strong> away from <strong>FREE delivery</strong></p>
                        <div className="fd-meter"><span style={{ width: `${Math.min(100, (total / FREE_DELIVERY_MIN) * 100)}%` }} /></div>
                        <button className="btn-outline fd-btn" onClick={() => { setPage('shop'); setCategory('All Products'); }}>+ Add more items</button>
                      </>
                    )}
                  </div>
                  <div className="order-summary">
                    <h3>Order Summary</h3>
                    <div className="summary-details">
                      <div className="summary-row"><span>Subtotal</span><span>₦{total.toLocaleString()}</span></div>
                      {discount > 0 && (<div className="summary-row discount"><span>Discount — {discountLabel} ({discount}%)</span><span>-₦{Math.floor(total * discount / 100).toLocaleString()}</span></div>)}
                      <div className={`summary-row ${deliveryFee === 0 ? 'shipping' : ''}`}><span>Delivery{zone ? ` (${zone.id === 'intl' ? 'international' : zone.label})` : ''}</span>{deliveryFee === null ? <span>Quoted on WhatsApp</span> : deliveryFee === 0 ? <span className="free">FREE</span> : <span>{naira(deliveryFee)}{!zone ? ' *' : ''}</span>}</div>
                      {!zone && <p className="admin-hint">* Owerri rate. Choose your area below for the exact fee.</p>}
                      <div className="summary-divider"></div>
                      <div className="summary-row total"><span>Total</span><span>₦{Math.floor(grandTotal).toLocaleString()}{deliveryFee === null ? ' + delivery' : ''}</span></div>
                    </div>
                  </div>
                  <div className="customer-form">
                    <h3>Your details</h3>
                    <input type="text" placeholder="Full name" autoComplete="name" value={customer.name} onChange={(e) => setCust('name', e.target.value)} />
                    <input type="tel" placeholder="Phone / WhatsApp number" autoComplete="tel" value={customer.phone} onChange={(e) => setCust('phone', e.target.value)} />
                    <select value={customer.zone} onChange={(e) => setCust('zone', e.target.value)}>
                      <option value="">Delivery area…</option>
                      {DELIVERY_ZONES.map(z => <option key={z.id} value={z.id}>{z.label}{z.fee ? ` — ${naira(z.fee)}` : z.fee === 0 ? ' — free' : ''}</option>)}
                    </select>
                    {customer.zone !== 'pickup' && <textarea rows={2} placeholder="Delivery address (street, area, landmark)" autoComplete="street-address" value={customer.address} onChange={(e) => setCust('address', e.target.value)} />}
                    <p className="admin-hint">Free delivery anywhere in Nigeria on orders of ₦100,000+.</p>
                  </div>
                  <div className="checkout-buttons">
                    <button className="btn-primary btn-checkout" onClick={checkout}>✓ Checkout on WhatsApp</button>
                    <button className="btn-secondary btn-invoice" onClick={() => generateInvoice(cart, total, finalTotal, promoCode)}>📄 Download Invoice</button>
                    <button className="btn-secondary btn-save" onClick={() => { alert('✓ Cart saved! You can continue shopping.'); setPage('shop'); }}>💾 Save & Continue</button>
                    <button className="btn-outline" onClick={() => setPage('shop')}>← Back to Shop</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ===== NEW: PRODUCT DETAIL MODAL ===== */}
      {selectedProduct && (
        <div className="modal-overlay" onClick={closeProductDetail}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeProductDetail}><X size={24} /></button>
            <div className="modal-grid">
              <div className="modal-image">
                <img src={imgSrc(selectedProduct.image)} alt={selectedProduct.name} />
              </div>
              <div className="modal-info">
                <h2>{selectedProduct.name}</h2>
                <div className="modal-category">📁 {selectedProduct.category}</div>
                <div className="modal-price-section">
                  <span className="modal-price">₦{selectedProduct.price?.toLocaleString()}</span>
                </div>
                {getProductVariants(selectedProduct.id).length > 0 && (
                  <div className="modal-variants">
                    <h4>Available Sizes:</h4>
                    <select className="modal-variant-select"
                      onChange={(e) => setSelectedVariant({...selectedVariant, [selectedProduct.id]: e.target.value})}
                      value={selectedVariant[selectedProduct.id] || ''}>
                      <option value="">Choose a variant...</option>
                      {getProductVariants(selectedProduct.id).map(v => (
                        <option key={v.id} value={v.id}>{v.variant_name} ({v.size}) - ₦{v.price?.toLocaleString()}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="modal-description">
                  <h4>About this product:</h4>
                  <p>{selectedProduct.description || `Premium quality ${selectedProduct.name} from Nkatanni Food Mart. Available in multiple sizes for your convenience.`}</p>
                </div>
                <div className="modal-qty">
                  <h4>Quantity:</h4>
                  <div className="qty-controls">
                    <button className="qty-btn" onClick={() => setModalQty(Math.max(1, modalQty - 1))}>−</button>
                    <input type="number" className="qty-input" value={modalQty} onChange={(e) => setModalQty(parseInt(e.target.value) || 1)} min="1" />
                    <button className="qty-btn" onClick={() => setModalQty(modalQty + 1)}>+</button>
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-primary modal-add-cart" disabled={isOutOfStock(selectedProduct)}
                    onClick={() => {
                      const variantId = selectedVariant[selectedProduct.id];
                      addToCart(selectedProduct, variantId ? parseInt(variantId) : null, modalQty);
                      closeProductDetail();
                    }}>{isOutOfStock(selectedProduct) ? 'Out of stock' : '🛒 Add to Cart'}</button>
                  <button className="btn-secondary modal-whatsapp"
                    onClick={() => {
                      const msg = `Hello Nkatanni, I'm interested in:\n\n${selectedProduct.name}\nQuantity: ${modalQty}\nPrice: ₦${(selectedProduct.price * modalQty).toLocaleString()}`;
                      window.open(`https://wa.me/2347055921564?text=${encodeURIComponent(msg)}`, '_blank');
                    }}>💬 Buy on WhatsApp</button>
                  <button className="btn-outline modal-share" onClick={() => shareProduct(selectedProduct)}>🔗 Share this product</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="footer-grid">
          <div className="footer-col">
            <h4>Nkatanni Food Mart</h4>
            <p>Your trusted online store for authentic Nigerian and African foodstuff & groceries.</p>
            <div className="footer-socials">
              <a href="https://facebook.com/nkatanni" target="_blank" rel="noreferrer">Facebook</a>
              <a href="https://instagram.com/nkatanni" target="_blank" rel="noreferrer">Instagram</a>
              <a href="https://tiktok.com/@nkatanni" target="_blank" rel="noreferrer">TikTok</a>
            </div>
          </div>
          <div className="footer-col">
            <h4>Shop</h4>
            <button onClick={() => setPage('shop')}>All Products</button>
            <button onClick={() => setPage('recipes')}>Recipes</button>
            <button onClick={() => setPage('hampers')}>Hampers</button>
            <button onClick={() => setPage('export')}>Export</button>
            <button onClick={() => setPage('faq')}>FAQs</button>
          </div>
          <div className="footer-col">
            <h4>Visit Us</h4>
            <p>Graceland Mall, Shop 117, Irete, Owerri</p>
            <p>Ritchie &amp; Hills Plaza, Suite 03, Egbeada, Owerri</p>
            <p>🕐 9AM–7PM daily</p>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <p>📞 +234 803 789 1888</p>
            <p>💬 WhatsApp: +234 705 592 1564</p>
            <button className="btn-primary footer-chat" onClick={() => window.open('https://wa.me/2347055921564', '_blank')}>Chat with us</button>
            <button className="btn-channel footer-channel" onClick={() => joinChannel('footer')}>📢 Join our WhatsApp Channel</button>
            <button className="btn-review footer-review" onClick={() => reviewUs('footer')}>⭐ Review us on Google</button>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 Nkatanni Food Mart | Owerri, Nigeria</p>
          {!adminLoggedIn && (
            <button className="footer-admin" onClick={() => {setPage('admin'); setAdminTab('products');}} title="Admin access">admin</button>
          )}
        </div>
      </footer>
    </div>
  );
}
